// ─────────────────────────────────────────────────────────────────────────────
// DTOs compartilhados entre API e Web. Importar por `@bi/types`.
//
// Mantenha aqui só os tipos que cruzam o limite HTTP. Tipos internos do
// backend (ex: shape do resolver de métricas) ou hooks do frontend NÃO entram.
// ─────────────────────────────────────────────────────────────────────────────

// ── Auth ────────────────────────────────────────────────────────────────────

export type UserRole = "admin" | "manutencao" | "gestor" | "consultor" | "visualizador";

export const USER_ROLE_LABELS: Record<UserRole, string> = {
  admin: "Admin", manutencao: "Manutenção", gestor: "Gestor", consultor: "Consultor", visualizador: "Visualizador",
};
export const PRIMARY_ADMIN_EMAIL = "admin@transmasi.com.br";

export interface AuthUser {
  id: string;
  companyId: string;
  name: string;
  email: string;
  role: UserRole;
  whatsappNumber: string | null;
  /** Empresa de demonstração (decidido no servidor). */
  isDemo?: boolean;
  /** Nome da empresa do usuário (usado no cabeçalho da DEMO). */
  companyName?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: AuthUser;
}

// ── Company / User CRUD ─────────────────────────────────────────────────────

export interface CompanyDto {
  id: string;
  name: string;
  legalName: string | null;
  cnpj: string | null;
  segment: string | null;
  status: "active" | "inactive" | "trial";
  plan: string | null;
}

// ── BI ──────────────────────────────────────────────────────────────────────

export type BiPeriodType =
  | "today"
  | "yesterday"
  | "current_week"
  | "last_week"
  | "current_month"
  | "previous_month"
  | "last_7_days"
  | "last_30_days"
  | "last_90_days"
  | "current_quarter"
  | "current_year"
  | "year_2026"
  | "custom";

export interface BiPeriod {
  type: BiPeriodType;
  from?: string; // ISO date — usado quando type = custom
  to?: string;
}

export interface BiComparison {
  enabled: boolean;
  type: "previous_period" | "previous_month" | "same_period_last_year" | null;
}

export interface BiInterpretation {
  intent:
    | "metric_query"
    | "financial_statement_query"
    | "ranking"
    | "executive_summary"
    | "alert_check"
    | "clarification_needed"
    | "out_of_scope";
  metric: string | null;
  period: BiPeriod;
  comparison: BiComparison;
  groupBy: string | null;
  filters: Array<{ field: string; op: string; value: string | number }>;
  wantsChart: boolean;
  confidence: number;
  clarificationPrompt?: string;
}

export type MessageChannel = "whatsapp" | "web";

export interface BiAskRequest {
  question: string;
}

/**
 * Estado estruturado de uma consulta FINANCEIRA (contas a pagar/receber).
 * Devolvido pelo pipeline e guardado na memória curta da conversa para que
 * refinamentos ("só com vencimento hoje") herdem direção/métrica/status.
 */
export interface BiFinancialState {
  metricKey: string;
  direction: "payable" | "receivable";
  dateField: "due_date" | "payment_date" | "receipt_date";
  status: "open" | "overdue" | "settled";
  period: BiPeriod | null;
  periodLabel?: string;
}

/** Tipo de saída esperado de uma consulta conversacional. */
export type QueryOutputType =
  | "single_metric"
  | "ranking"
  | "derived_calculation";

/** Resultado estruturado de um ranking (top clientes, inadimplentes, etc.). */
export interface RankingQueryResult {
  title: string;
  periodLabel: string;
  totalValue: number;
  /** Total formatado em pt-BR (ex: "R$ 2.327.450,80"). */
  totalFormatted: string;
  /** Nº de entidades distintas no período (clientes/devedores). */
  entityCount: number;
  /** Rótulo da unidade contável (ex: "notas", "títulos"). */
  countLabel?: string;
  items: Array<{
    rank: number;
    entityId: string | null;
    entityName: string;
    value: number;
    formattedValue: string;
    /** Participação no total (0..1). */
    percentageOfTotal: number | null;
    count?: number;
    oldestDueDays?: number;
  }>;
  dataSource: string;
  updatedAt: string;
  /** Aviso de confiabilidade da fonte (ex: contas a pagar incompletas). Omitido quando nulo. */
  notice?: string | null;
}

/** Resultado de um cálculo financeiro derivado (ex: fluxo de caixa do dia). */
export interface DerivedFinancialResult {
  title: string;
  periodLabel: string;
  inputs: Array<{
    key: string;
    label: string;
    value: number;
    formattedValue: string;
    count?: number;
    icon?: string;
  }>;
  formula: string;
  result: number;
  formattedResult: string;
  status: "positive" | "negative" | "neutral";
  /** Frase de leitura (ex: "Há mais pagamentos do que recebimentos hoje."). */
  reading?: string;
  updatedAt: string;
}

export interface BiAskResponse {
  questionId: string;
  answer: string;
  interpretation: BiInterpretation;
  data?: {
    /** Rótulo legível da métrica/consulta (ex: "Faturamento", "DRE gerencial"). */
    metricLabel?: string;
    primaryValue?: number | null;
    primaryFormatted?: string;
    comparisonValue?: number | null;
    comparisonFormatted?: string;
    variationPct?: number | null;
    breakdown?: Array<{ label: string; value: number; formatted: string }>;
  };
  /**
   * Recorte concreto efetivamente consultado (período resolvido + métrica +
   * valor). Usado para auditoria da conversa (WhatsApp/web) — garante que o
   * número respondido seja rastreável até a janela [from, to) usada na query.
   */
  resolved?: {
    metricKey: string | null;
    periodLabel: string | null;
    periodFrom: string | null;
    periodTo: string | null;
    value: number | null;
  };
  /** Presente quando a consulta foi do domínio financeiro (memória de contexto). */
  financialState?: BiFinancialState | null;
  /** Tipo de saída produzido (single_metric por padrão). */
  outputType?: QueryOutputType;
  /** Presente quando outputType = "ranking". */
  ranking?: RankingQueryResult | null;
  /**
   * Especificação do ranking respondido (base + limite + período) — alimenta a
   * memória curta para refinos encadeados ("e no ano de 2026?", "só os 3").
   */
  rankingSpec?: { base: string; limit: number; period: BiPeriod | null } | null;
  /** Presente quando outputType = "derived_calculation". */
  derived?: DerivedFinancialResult | null;
  chartUrl?: string | null;
}

// ── Dashboard ───────────────────────────────────────────────────────────────

export interface KpiCardDto {
  key: string;
  label: string;
  value: number;
  formatted: string;
  unit: "BRL" | "%" | "un";
  variationPct: number | null;
  comparisonLabel: string | null;
  trend: "up" | "down" | "flat";
}

export interface OverviewDashboardDto {
  generatedAt: string;
  period: { from: string; to: string; label: string };
  kpis: KpiCardDto[];
  topSellers: Array<{ name: string; total: number; formatted: string }>;
  topCategories: Array<{ name: string; total: number; formatted: string }>;
  dailyRevenue: Array<{ date: string; total: number }>;
}

// ── Transport dashboards (Fase 3J) ───────────────────────────────────────────

export interface TransportPeriodRow { from: string; to: string; label: string }

export interface TransportOverviewDto {
  generatedAt: string;
  period: TransportPeriodRow;
  kpis: KpiCardDto[];
  cteByDay: Array<{ date: string; count: number; totalValue: number }>;
  topCustomers: Array<{ name: string; cteCount: number; totalValue: number; formatted: string }>;
}

export interface TransportCteDashboardDto {
  generatedAt: string;
  period: TransportPeriodRow;
  comparisonLabel: string;
  summary: {
    count: number; totalValue: number; avgTicket: number; totalIcms: number;
    freightValue: number; tollValue: number; goodsValue: number; activeCustomers: number;
    totalQuantity: number; totalWeightKg: number;
  };
  prevSummary: {
    count: number; totalValue: number; avgTicket: number; totalIcms: number;
    freightValue: number; tollValue: number; goodsValue: number; activeCustomers: number;
    totalQuantity: number; totalWeightKg: number;
  };
  byDay: Array<{ date: string; count: number; totalValue: number }>;
  byStatus: Array<{ statusCode: number; count: number; totalValue: number }>;
  topCustomers: Array<{ name: string; cteCount: number; totalValue: number; formatted: string }>;
  ticketTrend: Array<{ month: string; avgTicket: number; count: number; totalValue: number }>;
  topVehicles: Array<{ plate: string; brand: string | null; modelYear: number | null; cteCount: number; totalValue: number; formatted: string }>;
}

export interface TransportFuelDashboardDto {
  generatedAt: string;
  period: TransportPeriodRow;
  comparisonLabel: string;
  summary: {
    /** Gasto de TODOS os combustíveis (diesel + arla + gasolina). */
    totalCost: number; liters: number; avgPricePerLiter: number;
    avgConsumption: number; costPerKm: number; km: number;
    count: number; activeVehicles: number;
    /**
     * Só diesel — é o recorte que bate com a "Relação de Abastecimentos" do
     * cliente, ao centavo e ao litro. É o número que o card principal exibe.
     */
    dieselCost: number; dieselLiters: number;
    /** Arla e gasolina, exibidos à parte para o card poder ser conferido. */
    otherFuelCost: number; otherFuelLiters: number;
    /**
     * Abastecimentos de diesel cujo intervalo de odômetro não cabe no mundo
     * físico e ficaram FORA do km e do km/l (ver apps/api/src/shared/fuel-km.ts).
     */
    discardedKmRefuels: number;
  };
  prevSummary: {
    totalCost: number; liters: number; avgPricePerLiter: number;
    avgConsumption: number; costPerKm: number; km: number; count: number;
    dieselCost: number; dieselLiters: number;
  };
  byDay: Array<{ date: string; cost: number; liters: number }>;
  costTrend: Array<{ month: string; cost: number; liters: number }>;
  byFuelType: Array<{ fuelType: string; cost: number; liters: number; formatted: string }>;
  topVehicles: Array<{
    plate: string; brand: string | null; cost: number; liters: number;
    km: number; costPerKm: number; consumption: number; formatted: string;
  }>;
}

export interface TransportTireDashboardDto {
  generatedAt: string;
  period: TransportPeriodRow;
  comparisonLabel: string;
  summary: {
    cost: number; recapCount: number; movementCount: number;
    activeCount: number; avgLifeKm: number; avgCostPerTire: number; nearChangeCount: number;
    /** Pneus cuja vida útil é INDISPONÍVEL (denominador inválido) — ficam fora dos alertas. */
    lifeUnknownCount: number;
    /** ACERVO de pneus recapados (estoque) — não confundir com `recapCount` (eventos no período). */
    recappedTireCount: number;
  };
  prevSummary: { cost: number; recapCount: number; movementCount: number };
  byDay: Array<{ date: string; cost: number }>;
  costTrend: Array<{ month: string; cost: number }>;
  topVehicles: Array<{ plate: string; cost: number; eventCount: number; formatted: string }>;
  byBrand: Array<{ brand: string; count: number; avgLifeKm: number }>;
  /** `basis` distingue desgaste medido por sulco (mm, Finatto) de km (SinerSoft). */
  nearChange: Array<{
    fireNumber: string; plate: string | null; size: string | null; pct: number;
    basis: "mm" | "km"; current: number; reference: number; unit: "mm" | "km";
  }>;
  /**
   * Gasto com pneus DERIVADO de fornecedores (estimativa — módulo frotas de pneus
   * inativo). `null` quando a fonte canônica é a Finatto: aí a estimativa por
   * contas a pagar não pode aparecer como métrica principal.
   */
  derivedSpend: {
    total: number; count: number; source: string; note: string;
    topSuppliers: Array<{ name: string; total: number; formatted: string }>;
  } | null;
  /**
   * ESTOQUE: valor residual do acervo de pneus ativos (≠ gasto no período).
   * `valor = custoDaVidaAtual × mmAtual / mmNovo`. `null` quando a fonte não
   * fornece sulco/custo por vida (SinerSoft).
   */
  valuation: {
    totalValue: number; newValue: number; recappedValue: number;
    newCount: number; recappedCount: number;
    valuedCount: number; unvaluedCount: number;
    formula: string;
  } | null;
  /** FLUXO: gasto no período apurado na própria fonte de pneus. */
  periodSpend: {
    /** Σ eventos de recapagem no período. */
    recapCost: number;
    /** `false` quando a origem não expõe compras por período (limitação, não zero). */
    purchasesAvailable: boolean;
    note: string;
  };
  /** Composição do acervo ativo por status da origem. `known:false` = legenda não confirmada. */
  byStatus: Array<{ code: string; label: string; known: boolean; count: number }>;
  /**
   * Painel "paridade Finatto": os cards do app nativo, POR UNIDADE (o app é
   * escopado à unidade logada — os números do cliente são da unidade 1).
   * `null` quando não há pneus Finatto.
   */
  finattoPanel: {
    units: Array<{
      unitCode: number;
      unitName: string | null;
      counts: {
        ativos: number; montados: number; disponiveis: number; emAnalise: number;
        analiseDescarte: number; reformador: number; emTransferencia: number;
        naoLocalizados: number; outros: number;
      };
      values: {
        ativos: number; rodando: number; novos: number; disponiveis: number;
        recapados: number; analiseEReformador: number; unvaluedCount: number;
      };
    }>;
    /** Veículos com pneus montados (enriquecimento DimVehicle). `null` = sync de posições nunca rodou. */
    vehicles: {
      withTires: number;
      positionsMissing: number;
      /** Veículos que a API Finatto não devolve (HTTP 500 — defeito A7). */
      note: string | null;
    } | null;
  } | null;
  /**
   * Gráficos "paridade Finatto" (widgets do dash nativo): milimetragem, vida,
   * medidas e DOT dos pneus ATIVOS; marcas das bandas de recape; descartes dos
   * últimos 12 meses por origem. `null` quando não há pneus Finatto.
   */
  finattoCharts: {
    mm: { buckets: Array<{ range: string; count: number }>; unknownCount: number };
    life: Array<{ stage: string; count: number }>;
    sizes: Array<{ size: string; count: number }>;
    dot: { years: Array<{ year: number; count: number }>; unknownCount: number };
    /** Marca da banda aplicada nos pneus recapados ativos (raw tipoRecape.marca). */
    recapBrands: Array<{ brand: string; count: number }>;
    /** Últimos 12 meses até o fim do período filtrado; `month` = "YYYY-MM". */
    discardsByMonth: Array<{ month: string; total: number; origins: Array<{ origin: string; count: number }> }>;
  } | null;
  /**
   * Desperdício ESTIMADO das baixas dos últimos 12 meses (cards "Valor/KM
   * desperdiçado" do dash Finatto — a API não expõe o cálculo original).
   * `null` quando não há pneus Finatto.
   */
  waste: {
    /** Σ valor residual (mm/mmNovo × custo da vida) congelado na baixa. */
    valueLost: number;
    /** Σ km projetado que a borracha restante ainda renderia. */
    kmLost: number;
    tiresDiscarded: number;
    valuedCount: number;
    kmEstimatedCount: number;
    note: string;
  } | null;
  /**
   * Retornos do reformador nos últimos 12 meses. Só `recape` é derivável hoje
   * (eventos de recapagem sincronizados); os demais tipos exigem o sync de
   * get_RetornoPneusReformador — `null` = indisponível, não zero.
   */
  supplierReturns: {
    recape: number;
    conserto: number | null;
    recusado: number | null;
    condenado: number | null;
    garantia: number | null;
    note: string;
  } | null;
  /** Fonte canônica configurada (env `FROTA_TIRE_SOURCE`). */
  activeSource: "sinersoft" | "finatto";
  /**
   * Diagnóstico quando a fonte ativa não tem dado nenhum — a tela precisa dizer
   * POR QUE está vazia em vez de exibir cards zerados.
   */
  sourceIssue: {
    kind: "no_data";
    message: string;
    /** Estado do último job de carga da base: running | completed | failed | null. */
    syncStatus: string | null;
  } | null;
  /** Origem dos pneus cadastrados (finatto = fonte canônica; sinersoft = legado). */
  bySource: Array<{ source: string; count: number }>;
  /** Há pneus vindos da API Finatto? (habilita colunas mm/vida/status na UI). */
  hasFinatto: boolean;
  /** Tabela de pneus (FASE 7) — colunas do spec, top 200 por custo. */
  tireDetails: Array<{
    fireNumber: string; brand: string | null; model: string | null; size: string | null;
    plate: string | null; positionCode: number | null; status: string | null; lifeStage: string;
    kmRun: number; currentMm: number | null; lifePct: number | null; recapCount: number;
    totalCost: number; costPerKm: number | null; source: string;
  }>;
}

export interface TransportMaintenanceDashboardDto {
  generatedAt: string;
  period: TransportPeriodRow;
  comparisonLabel: string;
  summary: {
    cost: number; count: number; partsCost: number; laborCost: number;
    avgCost: number; internalCost: number; externalCost: number;
  };
  prevSummary: { cost: number; count: number };
  byDay: Array<{ date: string; cost: number }>;
  costTrend: Array<{ month: string; cost: number }>;
  byOrigin: Array<{ origin: string; cost: number; count: number; formatted: string }>;
  topVehicles: Array<{ plate: string; cost: number; count: number; formatted: string }>;
}

/** origem do pedágio da placa: extrato importado, ou período sem extrato */
export type TollsSourceDto = "sem_parar" | "sem_extrato" | null;

/** quanto do período o extrato Sem Parar importado cobre (datas YYYY-MM-DD, inclusivas) */
export interface TollCoverageDto {
  status: "full" | "partial" | "none";
  coveredFrom: string | null;
  coveredTo: string | null;
}

export interface FleetOverviewDto {
  generatedAt: string;
  /** cobertura do extrato Sem Parar no período (aviso quando não é total) */
  tollCoverage: TollCoverageDto;
  period: TransportPeriodRow;
  comparisonLabel: string;
  summary: {
    totalCost: number; fuelCost: number; tireCost: number; maintenanceCost: number;
    km: number; costPerKm: number; activeVehicles: number;
    mostExpensivePlate: string | null; mostExpensiveCost: number; alertsCount: number;
  };
  prevSummary: { totalCost: number };
  composition: Array<{ category: string; cost: number; pct: number }>;
  costTrend: Array<{ month: string; fuel: number; tires: number; maintenance: number; total: number }>;
  vehicles: Array<{
    plate: string; brand: string | null; driver: string | null;
    revenue: number; revenueFormatted: string;
    fuel: number; tires: number; maintenance: number;
    /** comissão de acerto (legado; `logistica.acertos` está vazia nesta base) */
    commission: number;
    /** salário/comissão REAL do motorista nas viagens da placa no período */
    driverPay: number;
    /** pedágio pago na praça (extrato Sem Parar importado) */
    tolls: number;
    tollsSource: TollsSourceDto;
    /** custo de FROTA (combustível + manutenção + pneus) — base do custo/km e da composição */
    total: number; km: number; costPerKm: number; score: number; formatted: string;
    /**
     * custo DIRETO completo, idêntico ao da tela Margem por Placa
     * (combustível + manutenção + motorista + pedágio + despesas de viagem + seguro).
     * É por ele que o ranking é ordenado.
     */
    directCost: number;
    directCostFormatted: string;
    /** faturamento − custo direto */
    margin: number;
    marginFormatted: string;
    /** margem / faturamento; null sem receita */
    marginPct: number | null;
    /** sem combustível ou sem motorista no período — provavelmente parada; vai para o fim */
    idle: boolean;
    /**
     * carreta/reboque SEM cavalo conhecido — só custo, sem receita própria. Carreta com
     * vínculo já não aparece: foi consolidada no cavalo (ver `trailers`).
     */
    isTrailer: boolean;
    /** carretas do conjunto puxadas por este cavalo (custo delas já somado na linha) */
    trailers: string[];
    /** parcela do custo direto vinda das carretas do conjunto */
    trailerCost: number;
    status: "otimo" | "bom" | "atencao" | "critico";
  }>;
  alerts: Array<{ title: string; severity: "warning" | "critical" | "info"; plate: string | null; detail: string }>;
}

export interface TransportFinancialDto {
  generatedAt: string;
  comparisonLabel: string;
  // Scalar KPIs
  arOpenTotal: number;       // A Receber em Aberto
  arOverdueTotal: number;    // Inadimplência Total (AR vencido)
  apOpenTotal: number;       // A Pagar em Aberto
  apOverdueTotal: number;    // Pagamentos Atrasados (AP vencido)
  cashBalance: number;       // Saldo Bancário (soma dim_bank_accounts)
  receivedMtd: number;       // Recebido no Período (mês atual)
  receivedPrevMtd: number;   // mesmo janela no mês anterior (para variação)
  paidMtd: number;           // Pago no Período (mês atual)
  paidPrevMtd: number;       // mesmo janela no mês anterior (para variação)
  // Cash gap
  cashGap7d: { payable: number; receivable: number; gap: number };
  cashGap30d: { payable: number; receivable: number; gap: number };
  // Arrays
  arAging: Array<{ bucket: string; amount: number; formatted: string; count: number }>;
  /** aging de CONTAS A PAGAR — mesmas faixas do arAging (A vencer / 1-30 / 31-60 / 61-90 / 91+) */
  apAging: Array<{ bucket: string; amount: number; formatted: string; count: number }>;
  /**
   * Agenda de pagamento por faixa de vencimento (faixas MUTUAMENTE EXCLUSIVAS):
   * vencidas / vencem hoje / próximos 7 / 8–15 / 16–30 / acima de 30.
   * from/to = janela ISO da faixa ([from, to), to null = sem teto) p/ drill.
   */
  apSchedule: Array<{
    key: string; label: string; amount: number; formatted: string; count: number;
    from: string | null; to: string | null;
  }>;
  apByDueWeek: Array<{ week: string; amount: number; formatted: string; count: number }>;
  /**
   * Fonte das faixas/semanas de contas a pagar: `sinersoft` = consulta ao vivo no ERP
   * (mesma cláusula dos cards do topo → faixas fecham com os escalares); `warehouse` =
   * snapshot sincronizado (fallback quando o ERP está inacessível, pode divergir).
   */
  apScheduleSource: "sinersoft" | "warehouse";
  topDebtors: Array<{ name: string; overdueAmount: number; formatted: string; oldestDueDays: number }>;
  topSuppliers: Array<{ name: string; totalPaid: number; formatted: string }>;
  monthlyFlow: Array<{ month: string; received: number; paid: number }>;
  arOverduePrev1M: number;
  apOverduePrev1M: number;
  realizedNet30d: number;
}

export interface TransportFreightLetterDto {
  generatedAt: string;
  period: TransportPeriodRow;
  summary: { count: number; grossTotal: number; netTotal: number; advances: number };
  byDay: Array<{ date: string; gross: number; net: number }>;
  topDrivers: Array<{ name: string; gross: number; net: number; count: number }>;
}

export interface TransportSettlementDto {
  generatedAt: string;
  period: TransportPeriodRow;
  summary: { count: number; totalFreights: number; totalAdvances: number; totalExpenses: number; driverReceivable: number };
  byDriver: Array<{ name: string; totalFreights: number; driverReceivable: number; count: number }>;
}

export interface TransportCashflowDto {
  generatedAt: string;
  period: TransportPeriodRow;

  // ── Realizado no período selecionado ──────────────────────────────────────
  inflows: number;        // recebimentos (fact_receipts) no período
  outflows: number;       // pagamentos (fact_payments) no período
  netPeriod: number;      // inflows - outflows
  inflowsPrev: number;    // mesmo período anterior (variação)
  outflowsPrev: number;
  netPrev: number;

  // ── Posição em aberto (snapshot atual) ────────────────────────────────────
  arOpen: number;         // contas a receber em aberto
  apOpen: number;         // contas a pagar em aberto
  arOverdue: number;      // vencidos a receber
  apOverdue: number;      // vencidos a pagar
  cashBalance: number;    // saldo bancário atual (dim_bank_accounts)

  // ── Evolução diária dentro do período ─────────────────────────────────────
  daily: Array<{ date: string; inflow: number; outflow: number; net: number }>;

  // ── Composição ────────────────────────────────────────────────────────────
  topInflows: Array<{ name: string; amount: number; formatted: string }>;
  topOutflows: Array<{ name: string; amount: number; formatted: string }>;

  // ── Alertas determinísticos da tela ───────────────────────────────────────
  alerts: Array<{ severity: "critical" | "warning" | "info"; message: string }>;

  // ── Confiabilidade ────────────────────────────────────────────────────────
  apReliable: boolean;    // fact_payments inclui itens não operacionais
  warnings: string[];

  // ── Projeção (mantido p/ compatibilidade) ─────────────────────────────────
  projection: Array<{ period: string; payable: number; receivable: number; balance: number }>;
  ar30d: number;
  ap30d: number;
  gap30d: number;

  // ── Upgrade jul/2026 ──────────────────────────────────────────────────────
  /** curva DIÁRIA de projeção (D+0..D+60) da origem FactCashflowProjection */
  projectionDaily: Array<{ date: string; receivable: number; payable: number; balance: number }>;
  /** posição projetada 30d = saldo bancário atual + (a receber 30d − a pagar 30d) */
  positionProjected30d: number;
}

// ── Provisão de caixa por mês (fluxo de caixa) ──────────────────────────────

/** Um título em aberto que entra na provisão do mês. */
export interface CashflowProvisionTitle {
  /** chave do título na origem */
  key: string;
  /** data usada na provisão: previsão (quando preenchida e futura) ou vencimento */
  date: string;
  dueDate: string;
  /** true quando a data veio da previsão de recebimento/pagamento, não do vencimento */
  byExpectedDate: boolean;
  counterpart: string;
  amount: number;
  blocked: boolean;
}

/** Um dia do mês da provisão: a agenda do que vence, com o saldo que fica. */
export interface CashflowProvisionDay {
  /** "YYYY-MM-DD" */
  date: string;
  /** o dia já passou (só no mês corrente): o que venceu nele está no saldo ou nos vencidos */
  past: boolean;
  receivable: number;
  payable: number;
  receivableCount: number;
  payableCount: number;
  /** complemento estimado pela média, dividido igualmente pelos dias que faltam */
  recurringInflow: number;
  recurringOutflow: number;
  /** saldo projetado no fim do dia só com o lançado */
  closingBalance: number;
  /** saldo projetado no fim do dia com os recorrentes estimados */
  closingBalanceWithRecurring: number;
}

/** Semana do mês da provisão (1–7, 8–14, 15–21, 22–28, 29–fim). */
export interface CashflowProvisionWeek {
  label: string;
  from: string;
  to: string;
  /** a semana já passou (só no mês corrente): os fluxos dela já estão no saldo bancário */
  past: boolean;
  receivable: number;
  payable: number;
  /** complemento estimado pela média, distribuído pelos dias da semana */
  recurringInflow: number;
  recurringOutflow: number;
  /** saldo projetado no fim da semana só com o lançado */
  closingBalance: number;
  /** saldo projetado no fim da semana com os recorrentes estimados */
  closingBalanceWithRecurring: number;
}

export interface CashflowProvisionGroup {
  name: string;
  amount: number;
  count: number;
  /** 0..1 sobre o total do lado (a receber ou a pagar) */
  share: number;
}

/**
 * Provisão de caixa de um mês, a partir dos títulos de contas a receber e a pagar
 * já lançados (em aberto). Opcionalmente completada com "recorrentes estimados":
 * a diferença entre a média realizada dos últimos meses fechados e o que já está
 * lançado, para o mês não parecer mais folgado só porque ainda falta lançar contas.
 */
export interface CashflowProvisionDto {
  generatedAt: string;
  /** "YYYY-MM" */
  month: string;
  /** "dezembro de 2026" */
  monthLabel: string;
  isCurrentMonth: boolean;
  /** "YYYY-MM-DD" (data de hoje em Brasília) */
  today: string;
  /** saldo bancário atual */
  cashBalance: number;

  /** saldo projetado no início do mês: saldo de hoje + lançado entre hoje e o dia 1 */
  openingBalance: number;
  openingBalanceWithRecurring: number;
  /** saldo projetado no último dia do mês */
  closingBalance: number;
  closingBalanceWithRecurring: number;

  /** o que está lançado DENTRO do mês (só títulos com data a partir de hoje) */
  receivable: number;
  payable: number;
  receivableCount: number;
  payableCount: number;
  /** já realizado no mês corrente até hoje (0 em mês futuro) */
  realizedInflow: number;
  realizedOutflow: number;

  recurring: {
    /** há histórico realizado para estimar? */
    available: boolean;
    /** meses fechados usados na média, ex.: ["jul/26", "ago/26", "set/26"] */
    basisMonths: string[];
    avgInflow: number;
    avgOutflow: number;
    /** complemento estimado do mês: média − (lançado + realizado), nunca negativo */
    monthInflow: number;
    monthOutflow: number;
  };

  /** quanto do mês médio já está lançado (lançado + realizado ÷ média); null sem histórico */
  coverage: { inflowPct: number | null; outflowPct: number | null };

  weeks: CashflowProvisionWeek[];
  /** todos os dias do mês, do dia 1 ao último */
  days: CashflowProvisionDay[];
  topReceivable: CashflowProvisionGroup[];
  topPayable: CashflowProvisionGroup[];

  /** vencidos em aberto: fora da provisão porque não se sabe quando entram ou saem */
  overdue: { receivable: number; payable: number; receivableCount: number; payableCount: number };
  /** títulos do mês marcados como bloqueados na origem (estão na conta) */
  blocked: { receivable: number; payable: number; count: number };

  /** títulos do mês, por data (cortado em `limit` por lado) */
  titles: { receivable: CashflowProvisionTitle[]; payable: CashflowProvisionTitle[]; limit: number };

  warnings: string[];
}

// ── DRE gerencial por conta financeira ──────────────────────────────────────

/** Linha do DRE em que uma conta financeira cai. */
export type DreLineKey =
  | "receita_bruta" | "deducoes" | "custo_variavel" | "custo_frota" | "pessoal_op"
  | "adm" | "pessoal_adm" | "impostos" | "outras_rec" | "financeiro" | "fora";

/** Uma conta financeira dentro de uma linha do DRE (despesa com sinal negativo). */
export interface DreAccountLineDto {
  /** codigocfi; null = lançamento sem conta */
  code: number | null;
  mask: string | null;
  name: string;
  value: number;
  /** manual = classificada pela tela; regra = regra padrão por máscara/nome */
  classifiedBy: "manual" | "regra";
}

export interface DreByAccountLineDto {
  /** chave da linha (DreLineKey, "a_classificar" ou chave de subtotal) */
  key: string;
  label: string;
  kind: "line" | "subtotal" | "result";
  value: number;
  /** valor ÷ receita bruta; null sem receita */
  pctOfRevenue: number | null;
  /** contas que compõem a linha (vazio em subtotais) */
  accounts: DreAccountLineDto[];
  /** origem quando não é o rateio (ex.: "CT-e autorizados") */
  source?: string;
  /** as contas listadas são só informativas, não somam no valor da linha */
  accountsAreMemo?: boolean;
  note?: string;
}

/**
 * DRE gerencial com as despesas por conta financeira (rateio de contas a pagar,
 * data de entrada) e a receita do CT-e. Cada conta cai numa linha pela
 * classificação gravada ou pela regra padrão.
 */
export interface DreByAccountDto {
  generatedAt: string;
  period: { from: string; to: string; label: string };
  cteCount: number;
  lines: DreByAccountLineDto[];
  resultado: number;
  /** o que mexe no caixa mas não no resultado (financiamento, consórcio, estoque) */
  outside: { value: number; accounts: DreAccountLineDto[] };
  totals: { costs: number; revenue: number; icmsPaid: number };
  /** modo "Plano de contas": a mesma base em árvore pela máscara */
  tree: DreAccountTreeDto;
  lineOptions: Array<{ key: DreLineKey; label: string; description: string }>;
  warnings: string[];
}

/** Nó do plano de contas no modo "Plano de contas" (lista já em ordem de árvore). */
export interface DreAccountTreeNodeDto {
  key: string;
  parentKey: string | null;
  level: number;
  code: number | null;
  mask: string | null;
  name: string;
  /** T = totalizadora (soma as filhas), A = analítica */
  type: "T" | "A";
  nature: "R" | "D";
  /** valor positivo (despesa e receita), como no Demonstrativo do Sinersoft */
  value: number;
  pctOfRevenue: number | null;
  /** linha do DRE simplificado em que a conta analítica cai */
  dreLine: string | null;
}

export interface DreAccountTreeDto {
  nodes: DreAccountTreeNodeDto[];
  revenues: number;
  expenses: number;
  /** receitas − despesas, igual ao "Total Geral" do Demonstrativo (com sinal invertido) */
  result: number;
  outOfPlan: number;
}

export interface DreAccountClassificationResultDto {
  code: number;
  name: string;
  dreLine: DreLineKey | null;
  /** linha efetiva (gravada ou regra) */
  effectiveLine: DreLineKey | null;
}

// ── Inteligência (Insights + Alertas unificados) ────────────────────────────

export type IntelSeverity = "critical" | "warning" | "opportunity" | "info";
export type IntelArea = "financeiro" | "operacional" | "dre" | "fluxo_caixa" | "frota";

export interface IntelItem {
  id: string;
  title: string;
  category: string;             // ex: "Operacional / Receita"
  area: IntelArea;
  severity: IntelSeverity;
  periodLabel: string;          // ex: "01/06 → 03/06"
  mainValue: string;            // número principal já formatado
  comparison?: string | null;   // ex: "-18,4% vs. mês anterior"
  description: string;          // explicação curta
  recommendation?: string | null;
  source?: string | null;       // origem do dado
  // ── Camada de ação (opcional: consumidores antigos ignoram) ───────────────
  /** Tema do diagnóstico ao qual o item pertence. */
  theme?: IntelTheme | null;
  /** R$ em jogo, para ordenar prioridades (null = não monetizável). */
  impact?: number | null;
  /** Leitura do impacto, ex.: "R$ 1,16 mi a menos de receita". */
  impactLabel?: string | null;
  /** Título da lista de causas, ex.: "Clientes que mais caíram". */
  driversTitle?: string | null;
  /** Quem/o quê explica o número (calculado, nunca texto livre). */
  drivers?: IntelDriver[];
  /** Ações concretas, já com nomes e valores. */
  actions?: IntelAction[];
  /** Atalhos para a tela de origem e a memória de cálculo. */
  links?: Array<{ label: string; href: string }>;
}

export type IntelTheme = "caixa" | "receita" | "margem" | "frota" | "dados";

export interface IntelDriver {
  label: string;
  /** valor já formatado */
  value: string;
  detail?: string | null;
  /** 0..1, para a barra de participação */
  share?: number | null;
  tone?: "bad" | "good" | "neutral";
  href?: string | null;
}

export interface IntelAction {
  /** Frase completa (usada no "Copiar plano" e por consumidores antigos). */
  text: string;
  href?: string | null;
  // ── Passo detalhado (opcional) ────────────────────────────────────────────
  /** Verbo + objeto, curto: "Cobrar os 3 maiores devedores". */
  title?: string | null;
  /** Como fazer e por quê, em uma ou duas frases. */
  detail?: string | null;
  /** Área responsável sugerida: Financeiro, Comercial, Frota… */
  owner?: string | null;
  when?: "hoje" | "esta semana" | "este mês" | null;
  /** Resultado esperado, ex.: "até R$ 3,58 mi de volta ao caixa". */
  expected?: string | null;
  /** Com quem/o quê agir, com valores. */
  targets?: Array<{ label: string; value: string; note?: string | null; href?: string | null }>;
  /** Total real de alvos quando `targets` traz só os primeiros; a tela avisa "+N" em vez de cortar calado. */
  targetsTotal?: number | null;
  /** Rótulo do botão de `href`. */
  hrefLabel?: string | null;
}

/** Leitura do período: cruza os sinais em temas, só com números calculados. */
export interface IntelDiagnosis {
  headline: string;
  tone: "critical" | "warning" | "ok";
  themes: Array<{
    key: IntelTheme;
    title: string;
    summary: string;
    severity: IntelSeverity;
    impact: number | null;
    itemIds: string[];
  }>;
}

export interface IntelligenceDto {
  generatedAt: string;
  period: TransportPeriodRow;
  counts: { critical: number; warning: number; opportunity: number; info: number };
  items: IntelItem[];
  warnings: string[];           // avisos de dado parcial/insuficiente
  diagnosis?: IntelDiagnosis | null;
}

export interface TransportDreLine {
  key: string;
  label: string;
  value: number;
  formatted: string;
  /** "total" = linha totalizadora com fundo; "item" = sublinha; "result" = resultado final */
  role: "total" | "item" | "result";
  /** true = valor positivo na DRE (receita); false = valor negativo (custo/dedução) */
  isPositive: boolean;
  /** Fonte do dado, exibida como tooltip/legenda */
  source?: string;
  /** true quando o dado desta linha pode estar incompleto */
  unreliable?: boolean;
}

export interface DreDataCompleteness {
  cte: boolean;
  freightLetters: boolean;
  /** false = fact_payments inclui financiamentos — resultado operacional distorcido */
  operationalExpenses: boolean;
}

export interface TransportDreDto {
  generatedAt: string;
  period: TransportPeriodRow;

  // ── Escalares confiáveis ──────────────────────────────────────────────────
  grossRevenue:             number;  // CT-e totalValue (status=2)
  icmsDeduction:            number;  // CT-e icmsValue
  netRevenue:               number;  // grossRevenue - icmsDeduction
  driverFreightCost:        number;  // fact_freight_letters.gross_freight_value
  tollCost:                 number;  // pedágio pago (rateio do contas a pagar); sem rateio no período, fact_cte.toll_value
  /** de onde veio o tollCost */
  tollSource:               "rateio" | "cte";
  otherDirectCosts:         number;  // fact_cte.sec_cat_value + dispatch_value
  contributionMargin:       number;  // netRevenue - directCosts
  contributionMarginPct:    number;  // contributionMargin / netRevenue (0..1)
  cteCount:                 number;

  // ── Despesas (parcial / aviso) ────────────────────────────────────────────
  /** Pagamentos AP do período — inclui itens não-operacionais; usar com caveat */
  operationalExpenses:          number;
  operationalExpensesReliable:  boolean;

  // ── Resultado estimado ────────────────────────────────────────────────────
  netResult:   number;
  netMargin:   number | null;

  // ── Comparativos (período anterior) ──────────────────────────────────────
  prevGrossRevenue:          number;
  prevNetRevenue:            number;
  prevContributionMargin:    number;
  prevContributionMarginPct: number;
  prevNetResult:             number;
  comparisonLabel:           string;

  // ── Rankings ─────────────────────────────────────────────────────────────
  topCustomers: Array<{
    name: string;
    document: string | null;
    cteCount: number;
    revenue: number;
    formatted: string;
    sharePct: number;
  }>;

  // ── Tendência mensal (período selecionado + 5 anteriores) ─────────────────
  monthly: Array<{
    month: string;          // "YYYY-MM"
    grossRevenue: number;
    netRevenue: number;
    directCosts: number;
    contributionMargin: number;
    contributionMarginPct: number;
  }>;

  // ── Evolução do ano do período selecionado (Jan–Dez) ──────────────────────
  // Nome histórico: era sempre 2026. O ano real está em `month` ("YYYY-MM").
  yearly2026: Array<{
    month: string;          // "YYYY-MM"
    cteCount: number;
    grossRevenue: number;
    icmsDeduction: number;
    netRevenue: number;
    directCosts: number;
    contributionMargin: number;
    contributionMarginPct: number;
  }>;

  // ── Tabela DRE hierárquica ────────────────────────────────────────────────
  lines: TransportDreLine[];

  // ── Qualidade dos dados ───────────────────────────────────────────────────
  warnings: string[];
  dataCompleteness: DreDataCompleteness;
}

export interface TransportCostDto {
  generatedAt: string;
  period: TransportPeriodRow;
  // KPIs escalares
  totalPaid: number;          // Pagamentos realizados no período
  totalPaidPrev: number;      // mesmo período anterior (para variação)
  driverFreight: number;      // Frete motorista (factFreightLetter.grossFreightValue)
  driverFreightPrev: number;
  tollTotal: number;          // Pedágios (factCte.tollValue — CTes ativos)
  tollPrev: number;
  cteCount: number;           // CT-es ativos no período (para custo/CT-e)
  // Séries
  topSuppliers: Array<{ name: string; totalPaid: number; formatted: string }>;
  costByVehicle: Array<{ plate: string; freight: number; formatted: string }>;
  monthlyPayments: Array<{ month: string; paid: number }>;
}

/**
 * Custo por placa (financeiro) — replica o Demonstrativo por Conta Financeira /
 * Centro de Custo. Origem: FactVehicleCost (← financeiro.pagarrateios), independente
 * de FactAccountsPayable — cobre lançamentos pagos e em aberto (competência completa).
 * `hasData=false` enquanto o rateio não estiver sincronizado.
 */
export interface TransportVehicleCostDto {
  generatedAt: string;
  period: TransportPeriodRow;
  total: number;
  totalFormatted: string;
  byPlate: Array<{
    plate: string;
    total: number;
    formatted: string;
    byCategory: Array<{ category: string; total: number }>;
  }>;
  byCategory: Array<{ category: string; total: number; formatted: string }>;
  unallocated: {
    semPlaca: number;
    estoque: number;
    unresolved: number;
    /** quebra do "sem placa" por sub-grupo: administrativo/terceiros/operacional/frota_geral/outros */
    semPlacaBreakdown: Array<{ bucket: string; total: number; formatted: string }>;
  };
  hasData: boolean;
}

export type PlateStatus = "otimo" | "bom" | "atencao" | "critico";

/** Linha por placa da tela Margem por Placa (custo DIRETO = combustível+manutenção+comissão+seguro). */
export interface MarginPlateRow {
  plate: string;
  driver: string | null;
  revenue: number;
  revenueFormatted: string;
  fuel: number;
  maintenance: number;
  /** comissão de acerto (logistica.acertos) — legado, zerado nesta base */
  commission: number;
  /** custo REAL do motorista na viagem (controleviagens) */
  driverPay: number;
  /**
   * pedágio que compõe o custo direto: SÓ o pago na praça (extrato Sem Parar
   * importado). Com `tollsSource = "sem_extrato"` o período não tem extrato e o
   * valor é desconhecido (0 aqui, "sem extrato" na tela).
   */
  tolls: number;
  /** de onde saiu `tolls` */
  tollsSource: TollsSourceDto;
  /** pedágio pago na praça no período (extrato); 0 sem extrato */
  paidTolls: number;
  /** pedágio do frete de viagem no ERP (contratado/repassado) */
  freightTolls: number;
  /** despesas itemizadas da viagem (descarga, lavação, diária...) */
  tripExpenses: number;
  seguro: number;
  /** true quando `seguro` é o valor real da viagem (false = exposição estimada) */
  seguroIsReal: boolean;
  directCost: number;
  directCostFormatted: string;
  /** km rodados VAZIOS nas viagens da placa (eficiência de aproveitamento) */
  kmEmpty: number;
  margin: number;
  marginFormatted: string;
  /** margem / receita, escala 0..1; null sem receita */
  marginPct: number | null;
  km: number;
  /** receita / km; null sem km */
  revenuePerKm: number | null;
  /** custo direto / km; null sem km */
  costPerKm: number | null;
  /** km diesel / litros diesel; null sem base (Arla/gasolina fora do denominador) */
  kmPerLiter: number | null;
  /** litros de diesel abastecidos no período */
  dieselLiters: number;
  /** nº de abastecimentos diesel no período (base p/ confiabilidade do km/l) */
  dieselRefuels: number;
  cteCount: number;
  hasRevenue: boolean;
  /**
   * carretas do CONJUNTO puxadas por este cavalo (vínculo Sinersoft: reboque da viagem
   * ou `reboquevei` do cadastro). O custo delas já está somado nesta linha.
   */
  trailers: string[];
  /** parcela do custo direto que veio das carretas do conjunto */
  trailerCost: number;
  status: PlateStatus;
}

/**
 * Margem por placa — BI de Frota (redesign 2026-07). Rentabilidade operacional por
 * veículo: `margem = faturamento(FactCte st.2) − custo direto`, onde custo direto =
 * combustível (FactFuel) + manutenção (FactMaintenanceOrder) + comissão de motorista
 * (FactFreightSettlement) + seguro de carga por placa (exposição estimada). Pneus ficam
 * fora do custo direto (módulo dormente). Percentuais em escala 0..1.
 */
export interface TransportMarginByPlateDto {
  generatedAt: string;
  /** cobertura do extrato Sem Parar no período (aviso quando não é total) */
  tollCoverage: TollCoverageDto;
  period: TransportPeriodRow;
  comparisonLabel: string;
  summary: {
    totalRevenue: number;
    totalDirectCost: number;
    totalMargin: number;
    avgMarginPct: number | null;      // margem total / receita total
    revenuePerKm: number | null;      // receita total / km total
    /** km diesel / litros diesel da frota inteira; null sem base */
    fleetKmPerLiter: number | null;
    mostProfitablePlate: string | null;
    mostProfitableMargin: number;
    /** pedágio do custo direto (extrato importado) e em quantas placas */
    tollsFromInvoices: number;
    platesWithPaidTolls: number;
  };
  prevSummary: { totalRevenue: number; totalDirectCost: number; totalMargin: number; avgMarginPct: number | null; revenuePerKm: number | null };
  /** composição da margem sobre a receita: Receita 100% → custos → Margem Final */
  composition: Array<{ label: string; value: number; pct: number; kind: "revenue" | "cost" | "margin" }>;
  byPlate: MarginPlateRow[];
  /** 5 piores por margem % (só placas com receita) */
  worstMargin: Array<{ plate: string; driver: string | null; marginPct: number | null; status: PlateStatus }>;
  /**
   * 5 piores consumos (km/l diesel) com base mínima confiável (≥3 abastecimentos
   * diesel e ≥500 km no período). `vsFleetPct` = desvio vs. média da frota (0..1,
   * negativo = pior que a média); null quando não há média da frota.
   */
  worstConsumption: Array<{
    plate: string; driver: string | null; kmPerLiter: number;
    dieselLiters: number; dieselRefuels: number; km: number;
    vsFleetPct: number | null;
  }>;
  attention: Array<{ title: string; severity: "warning" | "critical" | "info"; plate: string | null }>;
  negativeCount: number;
  hasData: boolean;
}

/**
 * Raio-X da placa — visão única por veículo (produção, custos, resultado, operação,
 * pneus, alertas e leitura executiva). Números vêm dos MESMOS núcleos das telas de
 * frota (`computeFleetMargin`, `aggregateVehicleCosts`, `tireLifeUsed`) — equivalência
 * por construção. Percentuais em escala 0..1. `null` = sem base de cálculo (nunca 0).
 */
export interface VehicleDrilldownAlert {
  title: string;
  severity: "critical" | "warning" | "info";
  kind: string;
  /** evidência resumida (números que sustentam o alerta) */
  evidence: string;
  /** ação sugerida — catálogo fixo por tipo de exceção, nunca gerada por IA */
  recommendation: string | null;
}

/**
 * Memória de cálculo de um card do Raio-X da placa. Segue o padrão canônico
 * (docs/arquitetura/PADRAO_DRILLDOWN_METRICAS.md): valor DERIVADO expõe fórmula +
 * componentes + limitações; quando existe lista de documentos que o compõe, `detail`
 * aponta para ela (nunca finge composição que não existe).
 */
export interface VehicleMetricMemory {
  /** chave do card: revenue | km | margin | cost_per_km | km_per_liter | revenue_per_km */
  key: string;
  label: string;
  /** valor do card já formatado (mesma string exibida no KPI) */
  value: string;
  /** fórmula em linguagem de negócio */
  formula: string;
  /** parcelas que compõem a fórmula, na ordem em que ela é lida */
  components: Array<{ label: string; value: string; hint?: string | null }>;
  /** rótulo de negócio do campo de data usado no recorte */
  dateFieldLabel: string;
  calculationType: "realized" | "estimated" | "derived";
  /** tabelas analíticas de origem */
  sourceTables: string[];
  /** o que o número NÃO cobre (sempre explícito) */
  limitations: string[];
  /** documentos que compõem o número, quando existem nesta tela */
  detail: { kind: "ctes" | "fuel" | "maintenance"; label: string } | null;
}

export interface TransportVehicleDrilldownDto {
  generatedAt: string;
  /** cobertura do extrato Sem Parar no período (aviso quando não é total) */
  tollCoverage: TollCoverageDto;
  period: TransportPeriodRow;
  comparisonLabel: string;
  plate: string;
  driver: string | null;
  /** false quando a placa não tem NENHUM fato no período (página mostra vazio honesto) */
  found: boolean;

  /** conjunto cavalo↔carreta (vínculo Sinersoft) desta placa no período */
  rig: {
    /** carretas puxadas por este cavalo — custo delas já incluso nos números abaixo */
    trailers: string[];
    /** parcela do custo direto vinda das carretas */
    trailerCost: number;
    /**
     * preenchido quando a placa consultada É uma carreta: o cavalo em que ela foi
     * consolidada. A página mostra o aviso e manda o usuário para lá.
     */
    consolidatedInto: string | null;
  };

  /**
   * Odômetro da telemetria (GOBRAX), quando a placa tem rastreador.
   *
   * Fica SEPARADO do km do período de propósito: o odômetro do BI vem do
   * abastecimento (frentista digitando) e tem furo conhecido, enquanto este é
   * leitura de aparelho. A tela mostra os dois CREDITANDO a fonte, em vez de
   * substituir um pelo outro em silêncio.
   */
  telemetryOdometer: {
    km: number;
    /** instante da leitura na origem */
    at: string;
    /** provedor — a tela credita a fonte */
    source: string;
  } | null;

  /**
   * Auditoria de KM: abastecimento × odômetro da telemetria.
   *
   * O km do BI vem do `km_since_last` que o frentista digita; a telemetria lê o
   * odômetro do aparelho. Os dois lado a lado é o que permite auditar — não é
   * para substituir um pelo outro.
   *
   * `coverage` acompanha o número porque a telemetria só lê quando há evento: se
   * ela viu 10 dias de uma janela de 27, o km dela é menor por falta de leitura,
   * não por menos rodagem. `status` traduz isso.
   */
  kmAudit: {
    telemetryKm: number | null;
    fuelKm: number | null;
    diffKm: number | null;
    diffPct: number | null;
    coverage: { firstAt: string; lastAt: string; readings: number; coversPct: number } | null;
    kmPerLiterFuel: number | null;
    /** km da telemetria ÷ litros do SinerSoft — a melhor fonte de cada lado */
    kmPerLiterTelemetry: number | null;
    status: "ok" | "sem_telemetria" | "cobertura_parcial" | "divergencia_alta";
    source: string;
  } | null;

  /**
   * Auditoria de PEDÁGIO: pago na praça (extrato do fornecedor) × cobrado no
   * frete (`valorpedagiocvf` do ERP).
   *
   * São grandezas diferentes — uma é despesa efetiva, a outra é o que foi
   * contratado/repassado — e por isso nenhuma substitui a outra. A diferença é
   * o achado.
   *
   * `invoicePeriod` existe porque o ciclo da fatura NÃO é mês-calendário: a de
   * 08/2026 fecha em 22/08 e cobre desde 13/07. Comparar com o custo do mês sem
   * dizer o período real produziria uma divergência inventada por nós.
   */
  tollAudit: {
    /** pedágio pago, do extrato importado */
    paidTolls: number;
    /** passagens no período da fatura */
    tollCount: number;
    /** pedágio cobrado no frete, no período selecionado; null sem linha no núcleo */
    freightTolls: number | null;
    /** pago no período da TELA (passagens datadas) — é o que entrou na margem */
    paidInPeriod: number;
    paidInPeriodCount: number;
    diffValue: number | null;
    diffPct: number | null;
    /** período REAL coberto pela(s) fatura(s) somada(s) */
    invoicePeriod: { from: string; to: string } | null;
    /** true quando o período da fatura não cabe dentro do período da tela */
    periodMismatch: boolean;
    invoiceNumbers: string[];
    source: string;
  } | null;

  production: {
    revenue: number;
    cteCount: number;
    km: number;
    revenuePerKm: number | null;
  };

  costs: {
    /** custo direto operacional (mesma composição da tela Margem por Placa) */
    direct: {
      fuel: number; maintenance: number; commission: number;
      /** custo REAL do motorista na viagem (controleviagens) */
      driverPay: number;
      /** pedágio no custo direto (só o extrato Sem Parar importado) */
      tolls: number;
      tollsSource: TollsSourceDto;
      /** despesas itemizadas da viagem */
      tripExpenses: number;
      seguro: number;
      /** true = seguro real da viagem; false = exposição estimada */
      seguroIsReal: boolean;
      /** pneus (eventos) — informativo, FORA do custo direto da margem */
      tires: number;
      total: number;
    };
    /** custo financeiro alocado à placa (FactVehicleCost ← rateios de contas a pagar) */
    financial: {
      total: number;
      byCategory: Array<{ category: string; total: number }>;
      available: boolean;
    };
  };

  result: {
    margin: number;
    marginPct: number | null;
    costPerKm: number | null;
    status: PlateStatus;
    /** comparação com a média da frota no MESMO período */
    fleet: {
      avgMarginPct: number | null;
      costPerKm: number | null;
      revenuePerKm: number | null;
      kmPerLiter: number | null;
    };
    /** mesmo veículo no período anterior de igual duração */
    previous: {
      revenue: number; margin: number; marginPct: number | null;
      costPerKm: number | null; kmPerLiter: number | null;
      found: boolean;
    };
  };

  operation: {
    kmPerLiter: number | null;
    dieselLiters: number;
    dieselRefuels: number;
    /** km/l tem base mínima (≥3 abastecimentos diesel e ≥500 km)? */
    consumptionReliable: boolean;
    fuelHistory: Array<{
      date: string; liters: number; pricePerLiter: number | null;
      total: number; km: number | null; fuelType: string | null; station: string | null;
      /**
       * Hodômetro lido na bomba: início e fim do intervalo. São as colunas
       * "Km Inicial" e "Km Atual" da Lista de Abastecimentos do SinerSoft — o BI
       * já as sincronizava, mas não as exibia, então não dava para conferir o
       * `km` linha a linha contra o relatório.
       */
      odometerStart: number | null; odometerEnd: number | null;
      /** o intervalo entrou no KM da placa? (diesel e distância plausível) */
      countedInKm: boolean;
    }>;
    /** total de abastecimentos da placa no período (`fuelHistory` pode vir truncado) */
    fuelCount: number;
    maintenanceHistory: Array<{
      date: string; origin: "internal" | "external";
      parts: number; labor: number; total: number;
    }>;
    /** total de OS de manutenção no período (`maintenanceHistory` pode vir truncado) */
    maintenanceCount: number;
    tires: Array<{
      fireNumber: string | null; brand: string | null; model: string | null;
      position: string | null; status: string | null; lifeStage: string | null;
      currentMm: number | null; mmSafety: number | null;
      lifeUsedPct: number | null; recapCount: number | null;
    }>;
    tiresSource: string | null;
  };

  alerts: VehicleDrilldownAlert[];
  /** memória de cálculo de cada KPI da página (fórmula + componentes + limitações) */
  metricMemories: VehicleMetricMemory[];
  /** problemas de qualidade de dado desta placa (km ausente, placa genérica...) */
  dataQuality: string[];
  /** leitura executiva determinística: fato → evidência → comparação → indício */
  reading: string[];
  /** última sincronização por fonte usada nesta página */
  sources: Array<{ name: string; lastSyncAt: string | null }>;
}

/**
 * Linha do tempo operacional da placa — memória do card "KM Rodado".
 *
 * Explica o KM apurado no período em TRECHOS na ordem em que aconteceram, incluindo
 * os deslocamentos VAZIOS entre o destino de uma viagem e a origem da próxima.
 *
 * Duas grandezas que nunca se misturam: `plannedDistanceKm` é estimativa de rota e
 * `actualDistanceKm` é distância apurada por hodômetro. `null` = sem base (nunca 0).
 */
export type KmSegmentType =
  | "FREIGHT" | "REPOSITIONING" | "RETURN_TO_BASE" | "MAINTENANCE_TRANSFER" | "UNIDENTIFIED";

export type KmCalculationStatus = "CALCULATED" | "PENDING" | "ERROR" | "INSUFFICIENT_DATA";

export interface KmTimelineSegmentRow {
  sequence: number;
  type: KmSegmentType;
  typeLabel: string;
  /** "Curitiba/PR"; null quando o frete não trouxe cidade */
  origin: string | null;
  destination: string | null;
  departureAt: string | null;
  arrivalAt: string | null;
  plannedDistanceKm: number | null;
  actualDistanceKm: number | null;
  varianceKm: number | null;
  /** false no trecho vazio: rodou sem carga e sem faturamento */
  loaded: boolean;
  revenue: number;
  cteCount: number;
  calculationStatus: KmCalculationStatus;
  /** alertas de qualidade já em texto de negócio */
  warnings: string[];
  /** true quando o trecho foi ancorado na viagem anterior ao período */
  fromPreviousPeriod: boolean;
  tripId: number | null;
}

export interface VehicleKmTimelineDto {
  generatedAt: string;
  /** placa canônica; null quando o texto recebido não é placa */
  plate: string | null;
  from: string;
  to: string;
  /** viagens do período (a viagem anterior é contexto e não conta aqui) */
  tripCount: number;
  segments: KmTimelineSegmentRow[];
  reconciliation: {
    /** KM do card, apurado no hodômetro do abastecimento — a verdade a explicar */
    actualTotalKm: number;
    freightKm: number;
    repositioningKm: number;
    explainedKm: number;
    /** o que sobrou sem rota identificada — NÃO é rateado entre os trechos */
    unreconciledKm: number;
    plannedTotalKm: number | null;
    /** reposicionamento ÷ KM explicado (não sobre o KM apurado) — a tela precisa rotular */
    emptyPercent: number | null;
    tripCount: number;
    repositioningCount: number;
    segmentsWithoutDistance: number;
  };
  /** viagem anterior ao período que ancorou o primeiro reposicionamento */
  previousTripId: number | null;
  stats: {
    cteLinkedCount: number;
    cteMissingCount: number;
    routelessTripCount: number;
    freightlessTripCount: number;
  };
  /** o que esta apuração NÃO sabe — nunca escondido */
  limitations: string[];
  /**
   * Crédito do provedor de rotas, exigido pelos termos de uso. `null` quando nenhuma
   * distância estimada foi consultada — creditar quem não forneceu dado nenhum
   * confundiria a origem dos números da tela.
   */
  attribution: string | null;
}

/**
 * Comissão de motorista — Etapa 2, BI de Frota. Agrupa acertos (FactFreightSettlement)
 * por motorista. `commissionPct`/`avgCommissionPct` = comissão/fretes em escala 0..1
 * (derivado; a origem não tem coluna de percentual). Replica o relatório COMISSÃO MOTORISTAS.
 */
export interface TransportDriverCommissionDto {
  generatedAt: string;
  period: TransportPeriodRow;
  totalCommission: number;
  totalCommissionFormatted: string;
  totalFreights: number;
  totalFreightsFormatted: string;
  avgCommissionPct: number | null;
  driverCount: number;
  settlementCount: number;
  byDriver: Array<{
    name: string;
    commission: number;
    commissionFormatted: string;
    totalFreights: number;
    totalFreightsFormatted: string;
    commissionPct: number | null;
    settlementCount: number;
  }>;
  hasData: boolean;
}

/**
 * Seguro de carga — CICLO 2, BI de Frota. Estima seguro = valor da mercadoria
 * (FactCte.goodsValue) × taxa da apólice (% sobre a carga), e reconcilia com o
 * pago real (contas RCTR-C / "Seguro de Carga"). `ratePercent`/`effectiveRatePercent`
 * em escala PERCENTUAL (ex.: 0,30 = 0,30%). `isReference` = taxa placeholder a confirmar.
 */
export interface TransportInsuranceDto {
  generatedAt: string;
  period: TransportPeriodRow;
  goodsValueTotal: number;
  goodsValueTotalFormatted: string;
  ratePercent: number | null;
  rateSource: "override" | "policy" | "none";
  policyName: string | null;
  isReference: boolean;
  estimatedInsurance: number | null;
  estimatedInsuranceFormatted: string | null;
  actualCargoPaid: number;
  actualCargoPaidFormatted: string;
  effectiveRatePercent: number | null;
  gap: number | null;
  gapFormatted: string | null;
  byPlate: Array<{
    plate: string;
    goodsValue: number;
    goodsValueFormatted: string;
    estimated: number | null;
    estimatedFormatted: string | null;
    cteCount: number;
  }>;
  hasPolicy: boolean;
  hasData: boolean;
}

// ── Seguro de carga ───────────────────────────────────────────────────────
// Custo do seguro, peso na margem por cliente, vencimento das apólices e sinistros.

export type CargoPolicyKind = "rctr_c" | "rc_dc" | "rc_v" | "outra";

export interface CargoInsuranceAlertDto {
  severity: "critical" | "warning" | "info";
  title: string;
  text: string;
}

export interface CargoInsurancePremiumLineDto {
  kind: CargoPolicyKind;
  label: string;
  base: number;
  minimum: number;
  net: number;
  iof: number;
  total: number;
  note: string | null;
}

/** Quanto o seguro custa a um cliente no período (estimado pela taxa vigente em cada viagem). */
export interface CargoInsuranceCustomerDto {
  customerKey: string;
  label: string;
  goodsValue: number;
  cteCount: number;
  /** custo estimado do seguro (RCTR-C + RC-DC com IOF + RC-V da viagem) */
  cost: number;
  /** custo ÷ mercadoria; null sem mercadoria */
  costPct: number | null;
}

export interface CargoInsuranceClaimDto {
  id: string;
  policyKind: CargoPolicyKind;
  occurredAt: string;
  description: string;
  plate: string | null;
  lossValue: number | null;
  indemnityValue: number | null;
  status: "aberto" | "indenizado" | "negado";
}

export interface CargoInsuranceOverviewDto {
  generatedAt: string;
  asOf: string;
  period: TransportPeriodRow;
  alerts: CargoInsuranceAlertDto[];
  kpis: {
    goodsTotal: number;
    trips: number;
    expectedPremium: number;
    /** prêmio esperado ÷ mercadoria */
    costPctOfGoods: number | null;
    costPerTrip: number | null;
    /** prêmio esperado ÷ receita bruta dos CT-e */
    costPctOfRevenue: number | null;
  };
  premium: {
    lines: CargoInsurancePremiumLineDto[];
    expectedCargo: number;
    paidCargo: number;
    hasPaid: boolean;
    diff: number | null;
    impliedMissingGoods: number | null;
  };
  customers: CargoInsuranceCustomerDto[];
  claims: CargoInsuranceClaimDto[];
  hasRealPolicies: boolean;
}

export interface InsightsDashboardDto {
  generatedAt: string;
  bySeverity: { critical: number; warning: number; info: number };
  recentInsights: Array<{
    id: string; title: string; description: string; severity: string;
    generatedAt: string; ruleType: string | null; status: string;
  }>;
  trend7d: Array<{ date: string; count: number }>;
}

export interface DataQualityDashboardDto {
  generatedAt: string;
  /** hora da última varredura (null = nunca rodou) */
  lastRunAt: string | null;
  summary: { open: number; resolved: number; ignored: number };
  byEntity: Array<{ entity: string; open: number; critical: number; warning: number }>;
  byIssueType: Array<{ issueType: string; count: number; severity: string }>;
  recentIssues: Array<{
    id: string; entity: string; externalKey: string | null; issueType: string;
    severity: string; description: string; detectedAt: string; status: string;
  }>;
}

// ── Evolution webhook payload (subset usado pelo backend) ───────────────────

/** Linha de GET /data-quality/issues. */
export interface DataQualityIssueDto {
  id: string;
  entity: string;
  externalKey: string | null;
  issueType: string;
  severity: "critical" | "warning" | "info";
  description: string;
  evidenceJson: Record<string, unknown> | null;
  detectedAt: string;
  resolvedAt: string | null;
  status: "open" | "resolved" | "ignored";
}

export interface EvolutionWebhookPayload {
  event?: string;
  instance?: string;
  data?: {
    key?: {
      remoteJid?: string;
      remoteJidAlt?: string;   // número real quando remoteJid é @lid (LID addressing)
      addressingMode?: string; // "lid" | "phone"
      fromMe?: boolean;
      id?: string;
    };
    message?: {
      conversation?: string;
      extendedTextMessage?: { text?: string };
      audioMessage?: {
        url?: string;
        mimetype?: string;
        seconds?: number;
        ptt?: boolean; // push-to-talk (gravação de voz)
      };
    };
    pushName?: string;
    messageTimestamp?: number | string;
  };
  // a Evolution costuma anexar "sender" também — mantido aberto
  [key: string]: unknown;
}

// ── Conversational logs ─────────────────────────────────────────────────────

export interface BiQuestionLogDto {
  id: string;
  createdAt: string;
  userName: string | null;
  channel: "whatsapp" | "web";
  question: string;
  answer: string | null;
  status: "pending" | "interpreted" | "answered" | "failed" | "needs_clarification";
  metricKey: string | null;
  durationMs: number | null;
}

// ── Canal WhatsApp: visão de uso ──────────────────────────────────────────────

export interface WhatsAppChannelOverviewDto {
  /** janela em dias; null = todo o histórico */
  days: number | null;
  totals: {
    questions: number;
    answered: number;
    clarification: number;
    failed: number;
    whatsapp: number;
    web: number;
    /** respondidas / (todas), 0–1; null sem perguntas */
    answeredRate: number | null;
    avgDurationMs: number | null;
  };
  /** última pergunta de qualquer canal, em todo o histórico (mede abandono) */
  lastQuestionAt: string | null;
  firstQuestionAt: string | null;
  /** perguntas por semana (segunda-feira), mais antiga primeiro */
  weekly: Array<{ weekStart: string; count: number }>;
  topics: Array<{ label: string; count: number }>;
  /** quem tem acesso ao canal (ativo e perfil com conversa) */
  people: Array<{
    userId: string; name: string; role: string;
    /** número mascarado; null = sem número cadastrado, não consegue conversar */
    phoneMasked: string | null;
    questions: number;
    lastAt: string | null;
  }>;
  /** perguntas que o assistente não respondeu (erro ou pediu esclarecimento) */
  unanswered: Array<{
    id: string; question: string; status: "failed" | "needs_clarification";
    channel: "whatsapp" | "web"; createdAt: string; userName: string | null;
    /** o que o assistente respondeu / o erro, resumido */
    reply: string | null;
  }>;
}

export interface WhatsAppThreadDto {
  userId: string;
  name: string;
  channel: "whatsapp" | "web";
  messages: number;
  lastAt: string;
  lastText: string;
  lastDirection: "inbound" | "outbound";
}

export interface WhatsAppMessageDto {
  id: string;
  direction: "inbound" | "outbound";
  text: string;
  createdAt: string;
}

// ── Memória de Cálculo ────────────────────────────────────────────────────────

export type AuditStatus = "ok" | "warning" | "critical" | "error";

export interface CalculationMemoryBreakdownRow {
  groupKey: string;
  groupLabel: string;
  document: string | null;
  total: number;
  recordCount: number;
  percentageOfTotal: number;
}

export interface CalculationMemoryDetailRow {
  externalKey: string;
  dueDate: string;
  issueDate: string;
  openAmount: number;
  invoiceAmount: number;
  statusName: string;
  daysOverdue: number;
  /** valor já liquidado (original − aberto); null quando não se aplica */
  settledAmount?: number | null;
  /** contraparte (cliente/fornecedor) quando a linha não herda do grupo */
  counterpartyName?: string | null;
  /** placa (CT-e/carta frete) quando existir */
  plate?: string | null;
}

export type CalculationMemoryFilterType =
  | "status" | "date_range" | "overdue" | "balance" | "projection"
  | "due_window" | "overdue_window";

export interface CalculationMemoryDto {
  definition: {
    displayName: string;
    businessDefinition: string;
    formulaHuman: string;
    sourceTableAnalytic: string;
    sourceTableSinersoft: string;
    filterType: CalculationMemoryFilterType;
    /** "BRL" para monetário, "number" para contagem/peso/quantidade pura */
    displayUnit: "BRL" | "number";
    /** rótulo de negócio do campo de data usado (ex.: "vencimento", "recebimento") */
    dateFieldLabel: string;
    /** natureza do valor: realizado, previsto, vencido, a vencer, aberto, estimado */
    calculationType: "realized" | "forecast" | "overdue" | "due" | "open" | "estimated";
    /** o que fica de fora do cálculo, em linguagem de negócio */
    excludedStatus: string[];
    /** limitações conhecidas do número */
    limitations: string[];
    appliedFilters: {
      company: string;
      statusCodes: number[];
      periodFrom: string | null;
      periodTo: string | null;
      asOf: string;
    };
  };
  value: {
    total: number;
    recordCount: number;
    lastSyncAt: string | null;
    /** origem do total exibido: ERP ao vivo (canônico) ou tabela analítica do BI */
    source: "sinersoft" | "analytic";
    /** total da tabela analítica — é o que a lista de detalhes soma */
    analyticTotal: number;
    analyticRecordCount: number;
    /** diferença relativa entre o total exibido e a soma dos detalhes (0..1) */
    detailGapPct: number;
  };
  crossValidation: {
    status: AuditStatus;
    sourceValue: number | null;
    sourceRecordCount: number | null;
    variancePercentage: number | null;
    lastAuditAt: string | null;
    warningTolerance: number;
    criticalTolerance: number;
  };
  breakdown: {
    groupBy: string;
    rows: CalculationMemoryBreakdownRow[];
    pagination: {
      limit: number;
      offset: number;
      totalGroups: number;
    };
  };
}

export interface CalculationMemoryGroupDetailsDto {
  rows: CalculationMemoryDetailRow[];
  total: number;
  detailType: "open_item" | "movement" | "balance" | "projection";
}

// ═══════════════════════════════════════════════════════════════════════════
// Módulo de Manutenção e Checklist de Frota (operacional)
//
// Não confundir com `TransportMaintenanceDashboardDto` acima, que é o BI de
// CUSTO de manutenção vindo do ERP. Aqui é o checklist que a operação preenche.
// ═══════════════════════════════════════════════════════════════════════════

export type MaintenanceChecklistKindDto = "delivery" | "return_delivery" | "inspection" | "maintenance" | "daily" | "work_order";
export type MaintenanceImplementTypeDto = "sider" | "graneleiro" | "grade_baixa";
export type MaintenanceAnswerTypeDto =
  | "conformity" | "yes_no" | "quantity" | "number" | "text" | "photo" | "choice" | "damage_map";
export type MaintenanceCriticalityDto = "low" | "medium" | "high";
export type MaintenanceNonConformityTriggerDto =
  | "none" | "non_conforming" | "negative" | "below_minimum" | "flagged_option" | "damage_marked";

/** Opção de um item `choice`; `nonConforming` = escolhê-la abre ocorrência. */
export interface MaintenanceChoiceOptionDto {
  value: string;
  label: string;
  nonConforming?: boolean;
}

export type MaintenanceDamageViewDto = "left" | "right" | "front" | "rear";

/** Marca no desenho do veículo; x/y relativos (0–1) à vista. */
export interface MaintenanceDamageMarkDto {
  id: string;
  view: MaintenanceDamageViewDto;
  x: number;
  y: number;
  note: string | null;
}

/** `marks: []` = "sem avarias" (respondido). */
export interface MaintenanceDamageMapDto {
  marks: MaintenanceDamageMarkDto[];
}
export type MaintenanceRunStatusDto = "draft" | "in_progress" | "completed" | "cancelled";
export type MaintenanceNonConformityStatusDto = "open" | "in_analysis" | "resolved" | "cancelled";
export type MaintenanceWorkOrderStatusDto =
  | "open" | "scheduled" | "in_progress" | "waiting_parts" | "completed" | "cancelled";

export interface MaintenanceTemplateSummaryDto {
  id: string;
  code: string;
  name: string;
  description: string | null;
  kind: MaintenanceChecklistKindDto;
  implementType: MaintenanceImplementTypeDto | null;
  /** tipo de veículo/operação (Empilhadeira, Frota leve…) */
  vehicleType?: { id: string; name: string } | null;
  active: boolean;
  publishedVersion: { id: string; version: number; publishedAt: string | null } | null;
  itemCount: number;
}

// ── Cadastro de modelos (editor) — mesmo formato do catálogo em código ────

export interface MaintenanceTemplateItemDefDto {
  code: string;
  label: string;
  helpText?: string;
  answerType: MaintenanceAnswerTypeDto;
  required?: boolean;
  criticality?: MaintenanceCriticalityDto;
  requiresPhotoOnNonConformity?: boolean;
  allowsObservation?: boolean;
  nonConformityTrigger?: MaintenanceNonConformityTriggerDto;
  minQuantity?: number;
  options?: MaintenanceChoiceOptionDto[];
  defectOptions?: MaintenanceDefectOptionDto[];
  /** execução técnica (só modelos "Execução da O.S."); ausente = ✓ simples */
  execution?: MaintenanceExecutionConfigDto;
}

export interface MaintenanceTemplateSectionDefDto {
  code: string;
  title: string;
  helpText?: string;
  items: MaintenanceTemplateItemDefDto[];
}

export interface MaintenanceTemplateDefDto {
  code: string;
  name: string;
  description: string;
  kind: MaintenanceChecklistKindDto;
  implementType: MaintenanceImplementTypeDto | null;
  sections: MaintenanceTemplateSectionDefDto[];
}

export interface MaintenanceTemplateDefinitionDto {
  template: { id: string; active: boolean; vehicleTypeId: string | null };
  version: { id: string; version: number; publishedAt: string | null };
  /** execuções já feitas com o modelo (qualquer versão) */
  runCount: number;
  definition: MaintenanceTemplateDefDto;
}

export interface MaintenancePublishResultDto {
  templateId: string;
  code: string;
  outcome: "created" | "new_version" | "unchanged";
  version: number;
  sections: number;
  items: number;
}

export interface MaintenanceVehicleTypeDto {
  id: string;
  code: string;
  name: string;
  active: boolean;
  templateCount?: number;
}

export interface MaintenanceDefectFamilyDto {
  code: string;
  label: string;
  options: MaintenanceDefectOptionDto[];
}

export interface MaintenanceItemDto {
  id: string;
  code: string;
  label: string;
  helpText: string | null;
  order: number;
  answerType: MaintenanceAnswerTypeDto;
  required: boolean;
  criticality: MaintenanceCriticalityDto;
  requiresPhotoOnNonConformity: boolean;
  allowsObservation: boolean;
  nonConformityTrigger: MaintenanceNonConformityTriggerDto;
  minQuantity: number | null;
  /** só itens `choice` */
  options: MaintenanceChoiceOptionDto[] | null;
  /** problemas pré-cadastrados; escolher um é obrigatório quando o item abre ocorrência */
  defectOptions?: MaintenanceDefectOptionDto[] | null;
}

export interface MaintenanceDefectOptionDto {
  value: string;
  label: string;
  criticality?: MaintenanceCriticalityDto;
  /** o "Outro": exige descrição na observação */
  requiresObservation?: boolean;
}

export interface MaintenanceSectionDto {
  id: string;
  code: string;
  title: string;
  helpText: string | null;
  order: number;
  items: MaintenanceItemDto[];
}

export interface MaintenanceVersionDto {
  id: string;
  version: number;
  status: "draft" | "published" | "archived";
  publishedAt: string | null;
  sections: MaintenanceSectionDto[];
  template?: { id: string; code: string; name: string; kind: MaintenanceChecklistKindDto };
}

/** Último KM conhecido — SEMPRE com a fonte junto, nunca um número solto. */
export interface MaintenanceLastOdometerDto {
  km: number;
  source: "gobrax_series" | "gobrax_snapshot" | "fuel" | "checklist";
  sourceLabel: string;
  readAt: string | null;
}

export interface MaintenanceVehicleContextDto {
  plate: string;
  brand: string | null;
  vehicleTypeName: string | null;
  isTrailer: boolean;
  trailerPlate: string | null;
  status: string;
  /** implemento da placa: do cadastro do ERP ou lembrado pelo SIAG */
  implementType: MaintenanceImplementTypeDto | null;
  /** "cadastro" (ERP), "memoria" (classificação anterior no app) ou null */
  implementTypeSource: "cadastro" | "memoria" | "manual" | null;
  lastOdometer: MaintenanceLastOdometerDto | null;
}

export interface MaintenanceAnswerDto {
  id: string;
  itemId: string;
  sectionCode: string;
  itemCode: string;
  itemLabel: string;
  answerType: MaintenanceAnswerTypeDto;
  criticality: MaintenanceCriticalityDto;
  optionValue: string | null;
  numberValue: string | number | null;
  textValue: string | null;
  /** só `damage_map` */
  jsonValue: MaintenanceDamageMapDto | null;
  observation: string | null;
  defectCode?: string | null;
  defectLabel?: string | null;
  /** item de pneu: pneu/posição apontados (gestão de pneus) */
  tireId?: string | null;
  tirePositionId?: string | null;
  answeredAt: string;
}

export interface MaintenanceAttachmentDto {
  id: string;
  scope: "run" | "answer" | "non_conformity";
  runId: string | null;
  answerId: string | null;
  nonConformityId: string | null;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  caption: string | null;
  createdAt: string;
}

/** Foto de uma ocorrência: anexada no item do checklist (`answer`) ou na própria ocorrência. */
export interface MaintenanceEvidenceDto {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  caption: string | null;
  createdAt: string;
  origin: "answer" | "non_conformity";
}

export interface MaintenanceNonConformityDto {
  id: string;
  runId: string;
  answerId: string;
  itemCode: string;
  itemLabel: string;
  sectionCode: string;
  vehiclePlate: string;
  trailerPlate: string | null;
  driverName: string | null;
  criticality: MaintenanceCriticalityDto;
  /** problema pré-cadastrado apontado em campo */
  defectCode?: string | null;
  defectLabel?: string | null;
  description: string | null;
  status: MaintenanceNonConformityStatusDto;
  openedAt: string;
  resolvedAt: string | null;
  resolutionNotes: string | null;
  workOrderId: string | null;
  workOrder?: { id: string; number: number; status: MaintenanceWorkOrderStatusDto } | null;
  /** fotos da ocorrência + do item que a gerou */
  attachments?: MaintenanceEvidenceDto[];
  /** quem aplicou o checklist que abriu a ocorrência */
  inspectorName?: string | null;
  run?: { id: string; kind: MaintenanceChecklistKindDto; startedAt: string; finishedAt: string | null; odometerKm: number | null };
  /** ocorrência de pneu: qual pneu e em que posição (gestão de pneus; null nas antigas) */
  tireId?: string | null;
  tirePositionId?: string | null;
  tire?: TireRefDto | null;
  tirePosition?: TirePositionRefDto | null;
}

export type MaintenanceRunMovementDto = "departure" | "arrival";

export interface MaintenanceRunPairDto {
  id: string;
  kind: MaintenanceChecklistKindDto;
  movement: MaintenanceRunMovementDto | null;
  status: MaintenanceRunStatusDto;
  startedAt: string;
  finishedAt: string | null;
  odometerKm: number | null;
  driverName: string | null;
  trailerPlate: string | null;
}

export interface MaintenanceRunDto {
  id: string;
  templateId: string;
  templateVersionId: string;
  kind: MaintenanceChecklistKindDto;
  vehiclePlate: string;
  trailerPlate: string | null;
  driverId: string | null;
  driverName: string | null;
  status: MaintenanceRunStatusDto;
  startedAt: string;
  finishedAt: string | null;
  odometerKm: number | null;
  lastKnownOdometerKm: number | null;
  lastKnownOdometerSource: string | null;
  lastKnownOdometerAt: string | null;
  odometerOverrideReason: string | null;
  notes: string | null;
  relatedRunId: string | null;
  /** saída ou retorno; null = não se aplica (inspeção avulsa) */
  movement?: MaintenanceRunMovementDto | null;
  /** a saída que este retorno fecha */
  relatedRun?: MaintenanceRunPairDto | null;
  /** o(s) retorno(s) que fecharam esta saída */
  pairedRuns?: MaintenanceRunPairDto[];
  implementTypeSource?: "cadastro" | "manual" | null;
  /** usuário que aplicou o checklist */
  inspectorName?: string | null;
  template?: { name: string; code: string; implementType: MaintenanceImplementTypeDto | null };
  answers?: MaintenanceAnswerDto[];
  nonConformities?: MaintenanceNonConformityDto[];
  attachments?: MaintenanceAttachmentDto[];
  _count?: { answers: number; nonConformities: number };
}

/** Última contagem conhecida de um item de quantidade, na mesma placa. */
export interface MaintenancePreviousCountDto {
  itemCode: string;
  itemLabel: string;
  value: number;
  countedAt: string;
  runId: string;
  kind: MaintenanceChecklistKindDto;
}

export interface MaintenanceRunDetailDto {
  run: MaintenanceRunDto;
  version: MaintenanceVersionDto;
  /** por `itemCode` — o que o checklist anterior desta placa contou */
  previousCounts: Record<string, MaintenancePreviousCountDto>;
}

export interface MaintenanceDriverSearchDto {
  items: Array<{ id: string; name: string; document: string | null }>;
  /** "own" = só motoristas próprios (empregados); "all" = cadastro inteiro */
  scope: "own" | "all";
  ownCount: number;
}

export interface MaintenanceWorkOrderDto {
  id: string;
  number: number;
  origin: "checklist" | "manual" | "preventive";
  runId: string | null;
  vehiclePlate: string;
  trailerPlate: string | null;
  type: "preventive" | "corrective";
  description: string;
  priority: MaintenanceCriticalityDto;
  status: MaintenanceWorkOrderStatusDto;
  assignee: string | null;
  openedAt: string;
  dueAt: string | null;
  closedAt: string | null;
  odometerKm: number | null;
  cost: string | number | null;
  notes: string | null;
  executor: "internal" | "external";
  supplierName: string | null;
  vehicleStopped: boolean;
  startedAt: string | null;
  resolution: string | null;
  cancelReason: string | null;
  /** prazo vencido e O.S. não fechada (calculado no servidor) */
  overdue?: boolean;
  /** itens do plano preventivo que a O.S. atende */
  planComponents: string[];
  /** itens retirados na revisão: não fazem parte da O.S. e não geram execução */
  excludedComponents?: string[];
  nonConformities?: Array<{ id: string; itemLabel: string; criticality: MaintenanceCriticalityDto }>;
  _count?: { items: number };
}

export interface MaintenanceWorkOrderListDto {
  rows: MaintenanceWorkOrderDto[];
  total: number;
  /** por situação, + `active` (não fechadas) e `overdue` — os filtros valem, a situação não */
  counts: Record<MaintenanceWorkOrderStatusDto | "active" | "overdue", number>;
}

export interface MaintenanceWorkOrderItemDto {
  id: string;
  kind: "service" | "part";
  description: string;
  quantity: string | number;
  unitCost: string | number;
  totalCost: string | number;
  createdAt: string;
}

export type MaintenanceWorkOrderEventTypeDto =
  | "created" | "status" | "comment" | "item_added" | "item_removed" | "nc_linked" | "nc_unlinked" | "updated"
  /** KM da O.S. alterado (de → para, com o motivo na mensagem) */
  | "odometer";

export interface MaintenanceWorkOrderEventDto {
  id: string;
  type: MaintenanceWorkOrderEventTypeDto;
  fromStatus: MaintenanceWorkOrderStatusDto | null;
  toStatus: MaintenanceWorkOrderStatusDto | null;
  message: string | null;
  createdAt: string;
  createdByName: string | null;
}

export type MaintenanceWorkOrderChecklistStatusDto = "pending" | "done" | "not_done" | "not_applicable";

export type MaintenanceExecutionTypeDto = "simple_check" | "measurement" | "component" | "positional" | "tire_map";
export type MaintenanceInspectionConditionDto = "not_inspected" | "ok" | "attention" | "critical";
export type MaintenanceInterventionDto =
  | "no_action" | "adjusted" | "repaired" | "replaced" | "lubricated" | "cleaned" | "tightened" | "pending" | "not_applicable" | "other";

export interface MaintenanceExecFieldDto {
  name: string;
  label: string;
  type: "number" | "measurement" | "text" | "boolean" | "choice" | "multichoice" | "date";
  unit?: string;
  required?: boolean;
  min?: number;
  max?: number;
  warningThreshold?: number;
  criticalThreshold?: number;
  direction?: "higher_is_better" | "lower_is_better";
  options?: Array<{ value: string; label: string; icon?: string; axleTypes?: string[] }>;
}

export type MaintenancePositionsConfigDto =
  | { mode: "axles"; axles: number; dual?: boolean }
  | { mode: "list"; items: Array<{ code: string; label: string; group?: string; icon?: string }> }
  | { mode: "vehicle_axles"; vehicle?: "main" | "trailer" }
  | { mode: "tire_map" };

/** Configuração da execução técnica de um item (snapshot na O.S.). */
export interface MaintenanceExecutionConfigDto {
  type: Exclude<MaintenanceExecutionTypeDto, "simple_check">;
  /** como a tela apresenta o item: padrão, cartões ("o que foi trocado?") ou desenho da carreta por eixo */
  layout?: "default" | "tiles" | "axle_map";
  /** intervenção registrada ao marcar um cartão/eixo */
  implicitIntervention?: MaintenanceInterventionDto;
  fields: MaintenanceExecFieldDto[];
  interventions?: MaintenanceInterventionDto[];
  positions?: MaintenancePositionsConfigDto;
  requiresPhoto?: boolean;
  requiresObservationOnFailure?: boolean;
  requiresBeforeAfter?: boolean;
}

export interface MaintenanceInspectionStateDto {
  condition?: MaintenanceInspectionConditionDto;
  measures?: Record<string, unknown>;
}

export interface MaintenanceInspectionDto {
  id: string;
  positionKey: string;
  positionLabel: string | null;
  tireId: string | null;
  condition: MaintenanceInspectionConditionDto;
  autoCondition: MaintenanceInspectionConditionDto | null;
  interventions: MaintenanceInterventionDto[];
  measures: Record<string, unknown> | null;
  before: MaintenanceInspectionStateDto | null;
  after: MaintenanceInspectionStateDto | null;
  notes: string | null;
  pendingReason: string | null;
  pendingOwner: string | null;
  pendingDueAt: string | null;
  odometerKm: number | null;
  inspectedAt: string;
  finalizedAt: string | null;
  limitMessages: string[];
  attachments: Array<{ id: string; caption: string | null; fileName: string; mimeType: string }>;
}

export interface MaintenanceInspectionPositionDto {
  key: string;
  label: string;
  group: string | null;
  icon?: string | null;
  /** tipo do eixo (steering, traction, free, lift) e rodas duplas — quando as posições são os eixos do veículo */
  axleType?: string | null;
  dual?: boolean;
  tire: { tireId: string | null; code: string | null; brand: string | null; life: string | null; treadMm: number | null; pressurePsi: number | null; condition: string | null } | null;
}

export interface MaintenanceInspectionDeltaDto {
  field: string; label: string; unit: string | null; from: number; to: number; diff: number; km: number | null;
}

export interface MaintenanceInspectionPreviousDto {
  workOrderNumber: number | null;
  date: string | null;
  odometerKm: number | null;
  condition: MaintenanceInspectionConditionDto;
  interventions: MaintenanceInterventionDto[];
  measures: Record<string, unknown> | null;
  deltas: MaintenanceInspectionDeltaDto[];
}

export interface MaintenanceInspectionPanelDto {
  item: { id: string; itemKey: string | null; itemName: string; sectionName: string; executionType: MaintenanceExecutionTypeDto; required: boolean; status: MaintenanceWorkOrderChecklistStatusDto };
  workOrder: { id: string; number: number; status: MaintenanceWorkOrderStatusDto; vehiclePlate: string; odometerKm: number | null };
  config: MaintenanceExecutionConfigDto;
  positions: MaintenanceInspectionPositionDto[];
  inspections: MaintenanceInspectionDto[];
  previous: Record<string, MaintenanceInspectionPreviousDto>;
  completeness: { complete: boolean; missing: string[]; openProblems: number };
}

export type MaintenancePostConditionDto = "fit" | "fit_with_caveats" | "requires_intervention";

export interface MaintenanceTechnicalSummaryDto {
  inspected: number; ok: number; attention: number; critical: number; interventions: number; pending: number; open: number; criticalOpen: number;
}

export interface MaintenanceTechnicalOverviewDto {
  items: Record<string, { ok: number; attention: number; critical: number; inspected: number; expected: number | null; openProblems: number; pending: number }>;
  problems: Array<{
    inspectionId: string; checklistItemId: string; itemName: string; positionLabel: string | null;
    condition: MaintenanceInspectionConditionDto; state: "resolved" | "pending" | "open";
    reason: string | null; owner: string | null; dueAt: string | null; finalCondition: MaintenanceInspectionConditionDto;
  }>;
  summary: MaintenanceTechnicalSummaryDto;
  postCondition: MaintenancePostConditionDto | null;
  inspections: Array<{
    id: string; checklistItemId: string; itemName: string; positionKey: string; positionLabel: string | null; tireId: string | null;
    condition: MaintenanceInspectionConditionDto; interventions: MaintenanceInterventionDto[]; measures: Record<string, unknown> | null;
    before: MaintenanceInspectionStateDto | null; after: MaintenanceInspectionStateDto | null; notes: string | null;
    pendingReason: string | null; odometerKm: number | null; inspectedAt: string;
    attachments: Array<{ id: string; caption: string | null; fileName: string; mimeType: string }>;
  }>;
  /** rótulos e unidades dos campos de cada item técnico (snapshot) */
  fields: Record<string, Array<{ name: string; label: string; unit: string | null }>>;
}

export interface MaintenanceTechnicalReportDto {
  generatedAt: string;
  odometerKm: number | null;
  postCondition: MaintenancePostConditionDto;
  counts: MaintenanceTechnicalSummaryDto & { planned: number; executed: number };
  interventions: string[];
  pendings: string[];
  problems: Array<{ label: string; condition: MaintenanceInspectionConditionDto; state: "resolved" | "pending" | "open" }>;
  nextPreventive: Array<{ label: string; nextKm: number | null; nextDate: string | null }>;
  erpOrders: string[];
}

export interface MaintenanceTechnicalHistoryDto {
  plate: string;
  rows: Array<{
    id: string; workOrderId: string; workOrderNumber: number | null; date: string | null; odometerKm: number | null;
    itemKey: string; positionKey: string; positionLabel: string | null; tireId: string | null;
    condition: MaintenanceInspectionConditionDto; interventions: MaintenanceInterventionDto[];
    measures: Record<string, unknown> | null; before: MaintenanceInspectionStateDto | null; after: MaintenanceInspectionStateDto | null;
  }>;
}

/** Item do checklist de EXECUÇÃO da O.S. (snapshot do modelo na abertura). */
export interface MaintenanceWorkOrderChecklistItemDto {
  executionType: MaintenanceExecutionTypeDto;
  execution: MaintenanceExecutionConfigDto | null;
  itemKey: string | null;
  /** quem marcou (nome) */
  completedByName?: string | null;
  id: string;
  componentCode: string | null;
  sectionName: string;
  itemName: string;
  itemDescription: string | null;
  order: number;
  required: boolean;
  status: MaintenanceWorkOrderChecklistStatusDto;
  reason: string | null;
  notes: string | null;
  completedBy: string | null;
  completedAt: string | null;
}

export interface MaintenanceWorkOrderChecklistSummaryDto {
  total: number;
  done: number;
  notDone: number;
  notApplicable: number;
  pending: number;
  requiredPending: number;
  progressPct: number;
}

/** Como estava o plano na abertura da O.S. (congelado). */
export interface MaintenanceWorkOrderPlanSnapshotDto {
  capturedAt: string;
  group: { id: string; name: string } | null;
  components: Array<{
    componentCode: string;
    label: string;
    category: string | null;
    intervalKm: number | null;
    intervalDays: number | null;
    source: string | null;
    lastExecution: { executedAt: string; odometerKm: number | null; source: string } | null;
    nextKm: number | null;
    nextDate: string | null;
    status: string | null;
    currentKm: number | null;
  }>;
}

export interface MaintenanceErpOrderBriefDto {
  id: string;
  externalKey: string;
  number: string;
  origin: "internal" | "external";
  status: string;
  date: string;
  endDate: string | null;
  entryDate: string | null;
  plate: string | null;
  odometer: number | null;
  supplierName: string | null;
  total: number;
}

export interface MaintenanceWorkOrderErpLinkDto {
  id: string;
  status: "active" | "unlinked";
  matchScore: number | null;
  matchReason: string | null;
  linkedAt: string;
  linkedByName: string | null;
  unlinkedAt: string | null;
  unlinkedByName: string | null;
  unlinkReason: string | null;
  erpOrder: MaintenanceErpOrderBriefDto | null;
  erpExternalKey: string;
}

export interface MaintenanceErpCandidateDto extends MaintenanceErpOrderBriefDto {
  score: number;
  level: "alta" | "possivel" | "fraca";
  reasons: string[];
  linkedToWorkOrderId: string | null;
}

export interface MaintenanceChecklistBindingDto {
  id: string;
  vehicleGroupId: string | null;
  componentCode: string | null;
  templateId: string;
  templateName: string | null;
  groupName: string | null;
}

export interface MaintenanceWorkOrderDetailDto extends Omit<MaintenanceWorkOrderDto, "nonConformities"> {
  /**
   * Última leitura de KM conhecida da placa (telemetria GOBRAX, abastecimento ou
   * checklist) — referência para quem corrige o KM da O.S. Null sem leitura.
   */
  lastKnownOdometer: { km: number; source: string; sourceLabel: string; readAt: string | null } | null;
  /** checklist de execução da O.S. preventiva; `bound` = veio de um modelo vinculado ao plano */
  checklist: { items: MaintenanceWorkOrderChecklistItemDto[]; summary: MaintenanceWorkOrderChecklistSummaryDto; bound: boolean };
  planSnapshot: MaintenanceWorkOrderPlanSnapshotDto | null;
  /** visão da execução técnica (selos por item, problemas, condição pós-manutenção); nulo sem item técnico */
  technical: MaintenanceTechnicalOverviewDto | null;
  /** resumo técnico gerado na conclusão (imutável) */
  technicalReport: MaintenanceTechnicalReportDto | null;
  erpLinks: MaintenanceWorkOrderErpLinkDto[];
  vehicle: { brand: string | null; model: string | null; group: { id: string; name: string } | null; isTrailer: boolean } | null;
  /** grupo de veículos da placa e, quando não há, o motivo (a tela explica em vez de "—") */
  groupLookup: { reason: "found" | "not_in_any_group" | "excluded" | "group_inactive"; group: { id: string; name: string } | null };
  closedBy: string | null;
  closedByName: string | null;
  createdByName: string | null;
  totals: { parts: number; services: number; total: number };
  items: MaintenanceWorkOrderItemDto[];
  events: MaintenanceWorkOrderEventDto[];
  nonConformities: Array<{
    id: string;
    runId: string;
    itemCode: string;
    itemLabel: string;
    sectionCode: string;
    criticality: MaintenanceCriticalityDto;
    defectLabel?: string | null;
    description: string | null;
    status: MaintenanceNonConformityStatusDto;
    openedAt: string;
    resolvedAt: string | null;
    driverName: string | null;
    trailerPlate: string | null;
    attachments: MaintenanceEvidenceDto[];
    tire?: TireRefDto | null;
    tirePosition?: TirePositionRefDto | null;
  }>;
  /** gestão de pneus: movimentações registradas contra esta O.S. */
  tireMovements?: Array<{
    id: string;
    tireId: string;
    type: TireMovementTypeDto;
    occurredAt: string;
    sourcePlate: string | null;
    destinationPlate: string | null;
    odometerKm: number | null;
    reason: string | null;
    tire: TireRefDto | null;
    sourcePosition: TirePositionRefDto | null;
    destinationPosition: TirePositionRefDto | null;
  }>;
}

export interface MaintenanceSummaryDto {
  runsInProgress: number;
  runsCompletedInPeriod: number;
  nonConformitiesOpen: number;
  nonConformitiesCritical: number;
  workOrdersOpen: number;
  topPlates: Array<{ plate: string; count: number }>;
}

// ─── Portal Operacional de Frota e Manutenção ────────────────────────────────

export type MaintenanceCapabilityDto =
  | "maintenance.view"
  | "checklist.execute"
  | "nonconformity.treat"
  | "workorder.manage"
  | "workorder.checklist"
  | "workorder.inspect"
  | "workorder.erp_link"
  | "workorder.approve"
  | "workorder.start"
  | "workorder.complete"
  /** alterar o KM da O.S. (sempre com motivo) — só admin */
  | "workorder.odometer"
  | "template.manage";

export interface MaintenanceCapabilitiesDto {
  role: string;
  capabilities: MaintenanceCapabilityDto[];
}

/** Número que pode ainda não existir — nunca vira zero quando falta a fonte. */
export type MaintenanceAvailabilityDto<T> = { available: true; value: T } | { available: false; reason: string };

export interface MaintenancePortalDraftDto {
  id: string;
  templateName: string | null;
  kind: MaintenanceChecklistKindDto;
  vehiclePlate: string;
  trailerPlate: string | null;
  driverName: string | null;
  startedAt: string;
  answered: number;
}

export interface MaintenancePortalHomeDto {
  myDrafts: MaintenancePortalDraftDto[];
  runsInProgress: number;
  nonConformitiesOpen: number;
  nonConformitiesCritical: number;
  criticalToday: number;
  workOrdersOpen: number;
  workOrdersOverdue: number;
  tiresCritical: MaintenanceAvailabilityDto<number>;
  inspectionsOverdue: MaintenanceAvailabilityDto<number>;
  preventiveOverdue: MaintenanceAvailabilityDto<number>;
}

export interface MaintenanceVehiclePendingDto {
  plate: string;
  openNonConformities: number;
  criticalNonConformities: number;
  openWorkOrders: number;
}

export interface MaintenanceManagementOverviewDto extends Omit<MaintenancePortalHomeDto, "myDrafts"> {
  runsCompletedInPeriod: number;
  workOrdersByType: { preventive: number; corrective: number };
  recentRuns: Array<MaintenancePortalDraftDto & { status: MaintenanceRunStatusDto; finishedAt: string | null; nonConformities: number }>;
  vehiclesWithPending: MaintenanceVehiclePendingDto[];
  criticalWithoutWorkOrder: number;
  manualImplementClassification: { runs: number; plates: string[] };
}

export interface MaintenanceCriticalTireDto {
  id: string;
  fireNumber: string | null;
  plate: string | null;
  currentMm: number;
  safetyMm: number;
  brand: string | null;
  size: string | null;
}

export interface MaintenancePortalTiresDto {
  critical: MaintenanceCriticalTireDto[];
  inspection: { available: false; reason: string };
}

export type MaintenanceHistoryEventTypeDto = "checklist" | "non_conformity" | "work_order" | "erp_order" | "tire";

export interface MaintenanceHistoryEventDto {
  type: MaintenanceHistoryEventTypeDto;
  id: string;
  at: string;
  title: string;
  detail: string | null;
  status: string | null;
  severity: string | null;
  link: string | null;
}

export interface MaintenanceVehicleHistoryDto {
  vehicle: MaintenanceVehicleContextDto;
  pending: { openNonConformities: number; openWorkOrders: number; runsInProgress: number };
  tires: { mounted: number; critical: MaintenanceCriticalTireDto[] };
  events: MaintenanceHistoryEventDto[];
}

// ─── Plano preventivo ────────────────────────────────────────────────────────

export type MaintenancePlanStatusDto = "vencido" | "vencendo" | "em_dia" | "sem_base" | "sem_km" | "km_inconsistente";

export interface MaintenancePlanRowDto {
  id: string;
  plate: string;
  isTrailer: boolean;
  componentCode: string;
  label: string;
  category: string;
  intervalKm: number | null;
  intervalDays: number | null;
  /** erp | padrao | manual */
  source: "erp" | "padrao" | "manual" | "grupo";
  /** última troca segundo o ERP — só referência até alguém adotar */
  erpReference: { km: number; date: string } | null;
  lastExecution: { executedAt: string; odometerKm: number | null; source: "work_order" | "manual" | "erp_adotado"; workOrderId: string | null } | null;
  currentKm: MaintenanceLastOdometerDto | null;
  openWorkOrder: { id: string; number: number } | null;
  status: {
    status: MaintenancePlanStatusDto;
    nextKm: number | null;
    nextDate: string | null;
    kmRemaining: number | null;
    daysRemaining: number | null;
    kmStale: boolean;
    reason: string;
  };
  /** grupo de manutenção da placa e por que ela está nele */
  group?: { id: string; name: string; source: "manual" | "modelo" } | null;
  /** marca e modelo do cadastro (ERP) */
  vehicleModel?: string | null;
  /** antecedência do aviso em vigor (null = padrão) */
  warnKm?: number | null;
  warnDays?: number | null;
  /** detalhes técnicos configurados no grupo (óleo, quantidade, filtro, tempo…) */
  details?: Array<{ key: string; label: string; value: string; unit?: string | null }> | null;
}

// ── Grupos de veículos (placas escolhidas → plano preventivo) ─────────────

export interface MaintenanceVehicleGroupDto {
  id: string;
  code: string;
  name: string;
  description: string | null;
  active: boolean;
  planItemCount?: number;
  vehicleCount?: number;
}

export interface MaintenanceVehicleGroupListDto {
  groups: MaintenanceVehicleGroupDto[];
  vehiclesWithoutGroup: number;
  fleetSize: number;
}

/** Detalhe técnico aberto de um item do plano (óleo, quantidade, filtro, tempo, procedimento…). */
export interface MaintenanceTechnicalDetailDto {
  key: string;
  label: string;
  value: string;
  unit?: string | null;
}

export interface MaintenanceGroupPlanItemDto {
  componentCode: string;
  label: string;
  category: string;
  intervalKm: number | null;
  intervalDays: number | null;
  /** antecedência do aviso "próximo"; nulo = padrão (15 dias; KM: o menor entre 5.000 e 20%) */
  warnKm?: number | null;
  warnDays?: number | null;
  details?: MaintenanceTechnicalDetailDto[] | null;
}

export interface MaintenanceVehicleGroupDetailDto {
  group: MaintenanceVehicleGroupDto & { planItems: MaintenanceGroupPlanItemDto[] };
  vehicles: Array<{ plate: string; brand: string | null; model: string | null; isTrailer: boolean }>;
}

export interface MaintenanceGroupFleetDto {
  vehicles: Array<{
    plate: string;
    brand: string | null;
    model: string | null;
    isTrailer: boolean;
    year: number | null;
    typeName: string | null;
    group: { id: string; name: string } | null;
  }>;
}

export interface MaintenanceKnownComponentDto {
  code: string;
  label: string;
  category: string;
  intervalKm: number | null;
  intervalDays: number | null;
}

export interface MaintenancePlanListDto {
  rows: MaintenancePlanRowDto[];
  counts: Record<MaintenancePlanStatusDto, number>;
  plates: number;
  platesOverdue: number;
  categories: string[];
}

export interface MaintenancePlanPlateDto {
  plate: string;
  rows: MaintenancePlanRowDto[];
}


// ─── Painel gerencial da manutenção ──────────────────────────────────────────

export type MaintenancePlateHealthDto = "critico" | "atencao" | "ok";

export interface MaintenanceDashboardDto {
  period: { from: string; to: string };
  generatedAt: string;
  fleet: {
    tractors: number;
    trailers: number;
    stopped: Array<{ plate: string; workOrderId: string; number: number; since: string; days: number }>;
    /** % dos cavalos próprios não parados por O.S. */
    availabilityPct: number | null;
    health: Record<MaintenancePlateHealthDto, number>;
  };
  daily: {
    date: string;
    expected: number;
    done: number;
    missing: Array<{ plate: string; lastDailyAt: string | null }>;
    last7Pct: number | null;
  };
  workOrders: {
    opened: number;
    completed: number;
    cancelled: number;
    active: number;
    overdue: number;
    avgLeadTimeHours: number | null;
    avgRepairHours: number | null;
    cost: {
      total: number; parts: number; services: number; unitemized: number;
      internal: number; external: number; preventive: number; corrective: number;
      completedWithoutCost: number;
    };
    weekly: Array<{ week: string; opened: number; completed: number; cost: number }>;
  };
  preventive: {
    counts: Record<MaintenancePlanStatusDto, number>;
    platesOverdue: number;
    topOverdue: Array<{ plate: string; label: string; reason: string; openWorkOrder: { id: string; number: number } | null }>;
  };
  quality: {
    opened: number;
    byCriticality: { high: number; medium: number; low: number };
    criticalWithoutWorkOrder: number;
    avgResolutionHours: number | null;
    /** `defects`: problemas pré-cadastrados mais apontados no item (até 3) */
    topFailingItems: Array<{ itemCode: string; itemLabel: string; count: number; critical: number; defects: Array<{ label: string; count: number }> }>;
  };
  board: Array<{
    plate: string;
    isTrailer: boolean;
    health: MaintenancePlateHealthDto;
    reasons: string[];
    dailyToday: boolean | null;
    lastDailyAt: string | null;
    lastInspectionAt: string | null;
    openNonConformities: number;
    criticalNonConformities: number;
    activeWorkOrders: number;
    stoppedSince: string | null;
    preventiveOverdue: number;
    preventiveDueSoon: number;
    preventiveNoBase: number;
  }>;
}

// ── Memória de cálculo dos cards de Frota ──────────────────────────────────
//
// Um único contrato para todos os cards de Frota (Visão, Combustível, Pneus,
// Custo por placa, Margem, Motoristas, Seguro, Manutenção). O card abre a
// memória por `cardKey`; a API devolve o número recalculado a partir das MESMAS
// linhas e do MESMO filtro que o card usa — a tela confere e avisa se não fechar.
// Padrão em docs/arquitetura/PADRAO_DRILLDOWN_METRICAS.md.

export type FleetMemoryUnit =
  | "BRL" | "count" | "pct" | "km" | "liters" | "km_per_liter" | "brl_per_km" | "brl_per_liter" | "hours" | "text";

export type FleetMemoryColumnType =
  | "text" | "plate" | "date" | "datetime" | "BRL" | "number" | "km" | "liters" | "pct" | "hours" | "mm";

export interface FleetMemoryColumn {
  key: string;
  label: string;
  type: FleetMemoryColumnType;
}

export interface FleetMemoryRow {
  cells: Record<string, string | number | boolean | null>;
  /** false = a linha está no universo mas NÃO entra no número (o motivo vai em `note`) */
  included: boolean;
  note?: string | null;
  /** tela do SIAG que abre o registro (O.S., checklist, placa) */
  href?: string | null;
}

/** Parcela da fórmula. `memoryKey` abre a memória da parcela. */
export interface FleetMemoryComponent {
  label: string;
  value: number | null;
  unit: FleetMemoryUnit;
  hint?: string | null;
  memoryKey?: string | null;
}

export interface FleetCardMemoryDto {
  cardKey: string;
  title: string;
  /** o número do card, recalculado pela memória */
  value: number | null;
  unit: FleetMemoryUnit;
  /** quando o card mostra texto (ex.: a placa mais rentável) */
  valueText?: string | null;
  /** motivo de o número não existir (ex.: dado não integrado) */
  unavailableReason?: string | null;
  formula: string;
  definition: string;
  calculationType: "realized" | "estimated" | "derived" | "snapshot" | "count";
  /** campo de data usado no recorte, em linguagem de negócio; null = posição atual */
  dateField: string | null;
  period: { from: string; to: string } | null;
  sources: string[];
  filters: string[];
  limitations: string[];
  components: FleetMemoryComponent[];
  columns: FleetMemoryColumn[];
  rows: FleetMemoryRow[];
  /** linhas no universo (antes do corte de exibição) */
  rowCount: number;
  /** linhas que entram no número */
  includedCount: number;
  /** a lista devolvida foi cortada em `rowLimit` */
  truncated: boolean;
  rowLimit: number;
  /** coluna somada no rodapé (sobre TODAS as linhas incluídas, não só as exibidas) */
  total: { columnKey: string; value: number } | null;
  link: { label: string; href: string } | null;
  generatedAt: string;
}

// ── OS do ERP (SinerSoft) — lidas pelo sync, consultadas pela placa ─────────

export type MaintenanceErpOrderStatusDto = "aberta" | "encerrada" | "cancelada" | "lancada";

export interface MaintenanceErpOrderDto {
  id: string;
  externalKey: string;
  number: string;
  /** internal = oficina própria (ordensservicos); external = nota de terceiro (ordensservicosexterna) */
  origin: "internal" | "external";
  status: MaintenanceErpOrderStatusDto;
  openedAt: string;
  closedAt: string | null;
  entryDate: string | null;
  plate: string | null;
  odometer: number | null;
  supplierName: string | null;
  driverName: string | null;
  parts: number;
  labor: number;
  total: number;
}

export interface MaintenanceErpOrderListDto {
  rows: Array<MaintenanceErpOrderDto & { itemCount: number }>;
  total: number;
  totalValue: number;
}

export interface MaintenanceErpOrderDetailDto extends MaintenanceErpOrderDto {
  /** observação do ERP com dados de pagamento (PIX, CPF, conta) mascarados */
  notes: string | null;
  items: Array<{
    kind: "service" | "part";
    sequence: number;
    code: number | null;
    description: string;
    quantity: number;
    unitValue: number | null;
    totalValue: number | null;
  }>;
  /** plano preventivo da placa */
  plan: Array<{ componentCode: string; label: string; category: string; status: string; intervalKm: number | null; intervalDays: number | null }>;
  /** itens do plano que o texto da OS indica — sugestão, quem registra confirma */
  suggested: string[];
  /** itens do plano já registrados a partir desta OS */
  registered: string[];
}

export interface MaintenanceErpOrderRegisterResultDto {
  registered: Array<{ componentCode: string; ok: boolean; message?: string }>;
}


// ════════════════════════════════════════════════════════════════════════════
// GESTÃO DE PNEUS (/fleet/...) — camada operacional do SIAG sobre o espelho
// Finatto/SinerSoft. Não confundir com `TransportTireDashboardDto` (BI).
// ════════════════════════════════════════════════════════════════════════════

export type TireCapabilityDto = "tires.view" | "tires.inspect" | "tires.move" | "tires.retread" | "tires.manage";
export interface TireCapabilitiesDto { role: string; capabilities: TireCapabilityDto[] }

export type TireStatusDto = "installed" | "stock" | "retread" | "written_off" | "unknown";
export type TireConditionDto = "normal" | "attention" | "critical";
export type TireProblemDto = "low_pressure" | "irregular_wear" | "cut" | "bulge" | "low_tread" | "damage" | "other";
export type TireActionDto = "install" | "remove" | "rotate" | "transfer" | "to_stock" | "to_retread" | "from_retread" | "write_off";
export type TireMovementTypeDto =
  | "installation" | "removal" | "rotation" | "transfer" | "to_stock" | "from_stock"
  | "to_retread" | "from_retread" | "write_off" | "adjustment";
export type TireRetreadStatusDto = "waiting_send" | "sent" | "in_process" | "returned" | "rejected" | "condemned" | "warranty";
export type TireSituationDto = "all" | "installed" | "stock" | "retread" | "critical" | "attention" | "written_off" | "unknown";

export interface TireRefDto { id: string; code: string; brand: string | null }
export interface TirePositionRefDto { id: string; code: string; label: string }

export interface TireLocationDto {
  vehicleId: string | null;
  plate: string;
  positionId: string | null;
  positionCode: string | null;
  positionLabel: string | null;
  axleName: string | null;
  installedAt: string | null;
  installedOdometerKm: number | null;
  installationId: string | null;
  /** siag = instalação registrada no SIAG; snapshot = placa/posição da integração */
  origin: "siag" | "snapshot";
}

export interface TireViewDto {
  id: string;
  code: string;
  fireNumber: string | null;
  externalTireId: number;
  source: string;
  brand: string | null;
  model: string | null;
  size: string | null;
  tireType: string | null;
  life: string;
  recapCount: number;
  status: TireStatusDto;
  statusLabel: string;
  statusOrigin: string;
  statusSince: string | null;
  externalStatus: { code: string; label: string } | null;
  location: TireLocationDto | null;
  treadMm: number | null;
  treadOrigin: string | null;
  treadAt: string | null;
  mmNew: number | null;
  mmSafety: number | null;
  pressurePsi: number | null;
  condition: TireConditionDto | null;
  lastInspectionAt: string | null;
  kmAccumulated: number | null;
  purchaseValue: number;
  currentLifeCost: number | null;
}

export interface TireListDto {
  rows: TireViewDto[];
  total: number;
  page: number;
  pageSize: number;
  counts: Record<TireSituationDto, number>;
  facets: { brands: string[]; lives: string[] };
}

export interface TireOverviewDto {
  kpis: {
    total: number; installed: number; stock: number; retread: number; attention: number; critical: number;
    unknown: number; writtenOff: number; managedBySiag: number; purchaseValueActive: number;
  };
  byStatus: Array<{ status: TireStatusDto; label: string; count: number }>;
  byLife: Array<{ life: string; count: number }>;
  nearLimit: Array<{
    id: string; code: string; brand: string | null; treadMm: number | null; mmSafety: number | null;
    condition: TireConditionDto | null; status: TireStatusDto; plate: string | null; positionLabel: string | null;
  }>;
  costByBrand: Array<{ brand: string; value: number; count: number }>;
  kmByBrand: Array<{ brand: string; avgKm: number; sample: number }>;
  alerts: Array<{ key: string; severity: "critical" | "warning" | "info"; count: number; label: string }>;
  coverage: { vehiclesWithConfiguration: number; activeInstallations: number; inspectionsLast30d: number };
}

export interface TireInspectionRowDto {
  id: string;
  tireId?: string;
  tireCode?: string;
  inspectedAt: string;
  plate: string | null;
  positionLabel: string | null;
  condition: TireConditionDto;
  conditionLabel: string;
  treadDepthMm: number | null;
  pressurePsi: number | null;
  problems: Array<{ code: TireProblemDto; label: string }>;
  notes: string | null;
  odometerKm: number | null;
  inspectedBy: string | null;
  workOrderId: string | null;
  batchId?: string | null;
  by?: string | null;
  attachments?: Array<{ id: string; mimeType: string; fileName: string }>;
}

export interface TireRetreadRowDto {
  id: string;
  tireId?: string;
  tireCode?: string;
  sequence: number;
  status: TireRetreadStatusDto;
  source: string;
  supplierName: string | null;
  sentAt: string | null;
  returnedAt: string | null;
  sentOdometerKm: number | null;
  cost: number | null;
  retreadType: string | null;
  treadPattern: string | null;
  warrantyUntil: string | null;
  notes: string | null;
  open: boolean;
  daysOut?: number | null;
}

export interface TireDetailDto {
  tire: TireViewDto;
  purchaseDate: string | null;
  managedBySiag: boolean;
  allowedActions: Array<{ action: TireActionDto; label: string }>;
  km: { accumulated: number | null; accumulatedOrigin: string | null; siagInstallations: number; siagComplete: boolean; currentVehicleKm: number | null };
  retreadCount: { integration: number; siag: number; total: number };
  costs: { purchase: number; retreadsIntegration: number; retreadsSiag: number; total: number; costPerKm: number | null; kmBasis: number | null };
  installations: Array<{
    id: string; status: "active" | "removed"; plate: string; positionLabel: string | null;
    installedAt: string; installedOdometerKm: number | null; removedAt: string | null; removedOdometerKm: number | null;
    installationReason: string | null; removalReason: string | null; source: string; installedBy: string | null;
  }>;
  inspections: Array<Omit<TireInspectionRowDto, "tireId" | "tireCode" | "batchId" | "by" | "attachments">>;
  retreads: TireRetreadRowDto[];
}

export interface TireTimelineItemDto {
  at: string;
  kind: "movement" | "inspection" | "external";
  origin: string;
  title: string;
  detail: string | null;
  plate: string | null;
  positionLabel: string | null;
  km: number | null;
  by: string | null;
  workOrderId: string | null;
}

export interface TireMovementRowDto {
  id: string;
  tireId: string;
  tireCode: string;
  type: TireMovementTypeDto;
  typeLabel: string;
  occurredAt: string;
  sourcePlate: string | null;
  sourcePosition: string | null;
  destinationPlate: string | null;
  destinationPosition: string | null;
  odometerKm: number | null;
  reason: string | null;
  notes: string | null;
  origin: string;
  workOrderId: string | null;
  statusBefore: TireStatusDto | null;
  statusAfter: TireStatusDto | null;
  by: string | null;
}

export interface TireMapPositionDto {
  id: string;
  code: string;
  label: string;
  side: "left" | "right" | "center";
  slot: "single" | "outer" | "inner" | "spare";
  order: number;
  installation: { id: string; installedAt: string; installedOdometerKm: number | null; source: string } | null;
  tire: TireViewDto | null;
}

export interface TireMapAxleDto {
  id: string;
  code: string;
  name: string;
  order: number;
  axleType: "steering" | "traction" | "free" | "lift" | "spare";
  dualWheel: boolean;
  positions: TireMapPositionDto[];
}

export interface TireMapDto {
  vehicle: {
    id: string; plate: string; brand: string | null; model: string | null; vehicleTypeName: string | null;
    isTrailer: boolean; isOwn: boolean; status: string; finattoTireConfig: string | null; trailerPlate: string | null;
  };
  configuration: { id: string; code: string; name: string; vehicleKind: string; locked: boolean } | null;
  suggestedConfiguration: {
    source: "finatto" | "vehicle_type"; code: string; name: string; templateCode: string | null; finattoTireConfig: string | null;
  } | null;
  odometer: { km: number; source: string; sourceLabel: string; readAt: string | null; stale: boolean } | null;
  axles: TireMapAxleDto[];
  summary: { positions: number; occupied: number; empty: number; emptySpares: number; attention: number; critical: number };
  unplacedFromIntegration: TireViewDto[];
}

export interface TireConfigurationDto {
  id: string;
  code: string;
  name: string;
  vehicleKind: string;
  description: string | null;
  active: boolean;
  vehicleCount: number;
  positionCount: number;
  locked: boolean;
  axles: Array<Omit<TireMapAxleDto, "positions"> & { positions: Array<Omit<TireMapPositionDto, "installation" | "tire">> }>;
}

export interface TireConfigurationListDto {
  items: TireConfigurationDto[];
  templates: Array<{ code: string; name: string; vehicleKind: string; description: string | null; axleCount: number; spareCount: number }>;
}

export interface TirePositionMappingDto {
  id: string;
  source: string;
  externalPositionCode: number;
  externalLabel: string | null;
  positionCode: string | null;
  lastSeenAt: string | null;
  updatedAt: string;
  updatedBy: string | null;
  mountedTires: number;
}

export interface TireBootstrapReportDto {
  dryRun: boolean;
  labelsRecorded: number;
  configurationsCreated: number;
  vehiclesConfigured: number;
  installationsCreated: number;
  alreadyManaged: number;
  skipped: Record<"noVehicle" | "noConfiguration" | "unmappedPosition" | "positionTaken" | "duplicateInPosition", number>;
  unmapped: Array<{ tireId: string; code: string; plate: string; externalPosition: number | null; externalLabel: string | null; reason: string }>;
}

export interface TireInspectionRoundResultDto {
  batchId: string;
  vehiclePlate: string;
  summary: { total: number; normal: number; attention: number; critical: number };
  inspections: Array<{ id: string; tireId: string; condition: TireConditionDto }>;
}


// ─── BI da Central de manutenção ─────────────────────────────────────────────

export interface MaintenanceCentralDto {
  generatedAt: string;
  kpis: {
    preventivesSoon: number;
    preventivesOverdue: number;
    workOrdersOpen: number;
    vehiclesStopped: number;
    criticalItems: number;
    withoutReliableKm: number;
  };
  alerts: Array<{ plate: string; componentCode: string; message: string; amount: string; kind: "vencida" | "atencao" }>;
  alertsTotal: number;
  upcoming: Array<{
    plate: string; label: string; status: MaintenancePlanStatusDto;
    nextKm: number | null; nextDate: string | null; currentKm: number | null; kmRemaining: number | null; daysRemaining: number | null;
  }>;
  upcomingTotal: number;
  health: { emDia: number; atencao: number; vencida: number; semBase: number; total: number };
  costs: { months: Array<{ month: string; label: string; cost: number; workOrders: number }>; hasData: boolean };
  timeline: Array<{ id: string; number: number; plate: string; description: string; closedAt: string; type: "preventive" | "corrective" }>;
}
